import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Match, Player, Team, BatsmanStats, BowlerStats, Wicket, ScoreComponents, ScoreEntry } from '@/types/cricket';
import { toast } from '@/components/ui/use-toast';
import { updateMatchInnings, createWicket, fetchWickets, deleteWicket, createInnings, fetchInnings, updateInnings, updateInningsStats, getUmpire, fetchLiveMatchRoles, fetchMatchUmpires } from '@/services/supabaseService';
import { saveBattingStats, saveBowlingStats, loadBattingStats, loadBowlingStats, saveScoreHistory, loadScoreHistory, deleteScoreHistoryEntry, updateScoreHistoryEntry } from '@/services/matchStatsService';
import { StrikerSwapButton } from './StrikerSwapButton';
import { ScoreHistoryManager } from './ScoreHistoryManager';
import { Trash2, Undo, RefreshCw, Edit, Save, X, ChevronDown } from 'lucide-react';
import { MatchStatus } from './MatchStatus';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { MatchOverviewPanel } from './MatchOverviewPanel';
import { format } from 'date-fns'; // Import format from date-fns
import CricketGroundPanel from './CricketGroundPanel';
import MatchCompletionDialog from './MatchCompletionDialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { calculateRunRate, calculateExtrasFromScoreHistory, getTeamCaptain } from '@/utils/cricket/scoreCalculations';
import useSound from 'use-sound';
import overCompletedSound from '@/assets/over-completed.mp3';
import { Umpire } from '@/integrations/supabase/types';
import ballHitBatSound from '@/assets/ball-hit-bat.mp3';

interface LiveScoreboardProps {
  match: Match;
  teams: Team[];
  onScoreUpdate: (matchId: string, teamId: string, runs: number, wickets: number, overs: number, balls: number) => void;
  onMatchStatusChange: (matchId: string, status: 'upcoming' | 'live' | 'completed') => void;
  initialTab?: string;
}

const SHOT_TYPES = {
  FRONT_FOOT: [
    'cover_drive', 'straight_drive', 'on_drive', 'off_drive', 'defensive_push', 
    'flick', 'paddle_sweep', 'sweep', 'reverse_sweep', 'lap_sweep'
  ],
  BACK_FOOT: [
    'pull_shot', 'hook', 'cut_shot', 'square_cut', 'backfoot_punch', 
    'late_cut', 'upper_cut'
  ],
  LOFTED: [
    'lofted_drive', 'slog', 'slog_sweep', 'inside_out_drive', 'heave', 'chip_shot'
  ],
  OTHERS: [
    'dab', 'glance', 'scoop', 'ramp', 'switch_hit', 'no_shot_offered', 'missed'
  ]
} as const;

const SHOT_TYPE_DISPLAY = {
  cover_drive: 'Cover Drive',
  straight_drive: 'Straight Drive',
  on_drive: 'On Drive',
  off_drive: 'Off Drive',
  defensive_push: 'Defensive Push',
  flick: 'Flick',
  paddle_sweep: 'Paddle Sweep',
  sweep: 'Sweep',
  reverse_sweep: 'Reverse Sweep',
  lap_sweep: 'Lap Sweep',
  pull_shot: 'Pull Shot',
  hook: 'Hook',
  cut_shot: 'Cut Shot',
  square_cut: 'Square Cut',
  backfoot_punch: 'Backfoot Punch',
  late_cut: 'Late Cut',
  upper_cut: 'Upper Cut',
  lofted_drive: 'Lofted Drive',
  slog: 'Slog',
  slog_sweep: 'Slog Sweep',
  inside_out_drive: 'Inside-Out Drive',
  heave: 'Heave',
  chip_shot: 'Chip Shot',
  dab: 'Dab',
  glance: 'Glance',
  scoop: 'Scoop',
  ramp: 'Ramp',
  switch_hit: 'Switch Hit',
  no_shot_offered: 'No Shot Offered',
  missed: 'Missed'
} as const;

const BALL_LENGTHS = [
  'yorker',
  'full_toss',
  'full_length',
  'good_length',
  'short_of_length',
  'short_ball',
  'bouncer',
  'half_volley'
] as const;

const BALL_LENGTH_DISPLAY = {
  yorker: 'Yorker',
  full_toss: 'Full Toss',
  full_length: 'Full Length',
  good_length: 'Good Length',
  short_of_length: 'Short of Length',
  short_ball: 'Short Ball',
  bouncer: 'Bouncer',
  half_volley: 'Half Volley'
} as const;

const BALL_TYPES_SEAMERS = [
  'outswinger',
  'inswinger',
  'off_cutter',
  'leg_cutter',
  'slower_ball',
  'knuckle_ball'
] as const;

const BALL_TYPES_SPINNERS = [
  'off_break',
  'leg_break',
  'googly',
  'doosra',
  'carrom_ball',
  'topspin',
  'arm_ball',
  'slider',
  'flipper'
] as const;

const BALL_TYPE_DISPLAY = {
  outswinger: 'Outswinger',
  inswinger: 'Inswinger',
  off_cutter: 'Off Cutter',
  leg_cutter: 'Leg Cutter',
  slower_ball: 'Slower Ball',
  knuckle_ball: 'Knuckle Ball',
  off_break: 'Off Break',
  leg_break: 'Leg Break',
  googly: 'Googly',
  doosra: 'Doosra',
  carrom_ball: 'Carrom Ball',
  topspin: 'Topspin',
  arm_ball: 'Arm Ball',
  slider: 'Slider',
  flipper: 'Flipper'
} as const;

const FIELDING_POSITIONS_OFF_SIDE = [
  'slip', 'first_slip', 'second_slip', 'third_slip',
  'gully', 'point', 'deep_point',
  'cover', 'extra_cover', 'deep_cover',
  'mid_off', 'long_off',
  'third_man', 'deep_third'
] as const;

const FIELDING_POSITIONS_LEG_SIDE = [
  'leg_slip', 'short_leg', 'silly_point',
  'square_leg', 'deep_square_leg', 'backward_square_leg',
  'mid_wicket', 'deep_mid_wicket',
  'mid_on', 'long_on',
  'fine_leg', 'deep_fine_leg', 'leg_gully'
] as const;

const FIELDING_POSITIONS_SPECIAL = [
  'fly_slip', 'silly_mid_on', 'silly_mid_off', 'sweeper_cover', 'cow_corner',
  'wide_long_on', 'wide_long_off', 'long_stop',
  'others'
] as const;

const FIELDING_POSITION_DISPLAY = {
  slip: 'Slip',
  first_slip: 'First Slip',
  second_slip: 'Second Slip',
  third_slip: 'Third Slip',
  gully: 'Gully',
  point: 'Point',
  deep_point: 'Deep Point',
  cover: 'Cover',
  extra_cover: 'Extra Cover',
  deep_cover: 'Deep Cover',
  mid_off: 'Mid Off',
  long_off: 'Long Off',
  third_man: 'Third Man',
  deep_third: 'Deep Third',
  leg_slip: 'Leg Slip',
  short_leg: 'Short Leg',
  silly_point: 'Silly Point',
  square_leg: 'Square Leg',
  deep_square_leg: 'Deep Square Leg',
  backward_square_leg: 'Backward Square Leg',
  mid_wicket: 'Mid Wicket',
  deep_mid_wicket: 'Deep Mid Wicket',
  mid_on: 'Mid On',
  long_on: 'Long On',
  fine_leg: 'Fine Leg',
  deep_fine_leg: 'Deep Fine Leg',
  leg_gully: 'Leg Gully',
  fly_slip: 'Fly Slip',
  silly_mid_on: 'Silly Mid-On',
  silly_mid_off: 'Silly Mid-Off',
  sweeper_cover: 'Sweeper Cover',
  cow_corner: 'Cow Corner',
  wide_long_on: 'Wide Long On',
  wide_long_off: 'Wide Long Off',
  long_stop: 'Long Stop',
  others: 'Other Position'
} as const;

const INNINGS_END_REASONS = [
  'overs_completed',
  'wickets_completed',
  'target_achieved',
  'declared',
  'forfeited',
  'weather_interruption',
  'bad_light',
  'ground_conditions',
  'others'
] as const;

const INNINGS_END_REASON_DISPLAY = {
  overs_completed: 'Overs Completed',
  wickets_completed: 'All Wickets Lost',
  target_achieved: 'Target Achieved',
  declared: 'Innings Declared',
  forfeited: 'Innings Forfeited',
  weather_interruption: 'Weather Interruption',
  bad_light: 'Bad Light',
  ground_conditions: 'Ground Conditions',
  others: 'Other Reason'
} as const;

// Add this utility function at the top of the component (after imports):
const capitalizeFirst = (str: string | undefined) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

// Add this utility function near the top, after imports:
const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Add this type for fetched umpire assignments
interface MatchUmpireWithDetails {
  role: string;
  umpire_id: string;
  umpires?: {
    umpire_id: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
  };
}

export const LiveScoreboard: React.FC<LiveScoreboardProps> = (props) => {
  const { match, teams, onScoreUpdate, onMatchStatusChange, initialTab } = props;
  // Function to load all relevant match data
  const loadMatchData = useCallback(async () => {
    try {
      const matchWickets = await fetchWickets(match.id);
      setWickets(matchWickets);

      const battingData = await loadBattingStats(match.id);
      const bowlingData = await loadBowlingStats(match.id);
      const historyData = await loadScoreHistory(match.id);

      setBatsmanStats(battingData);
      setBowlerStats(bowlingData);
      setScoreHistory(historyData);

      const onStrikeBatsman = battingData.find(b => b.isOnStrike);
      if (onStrikeBatsman) {
        setCurrentStriker(onStrikeBatsman.playerId);
        const nonStriker = battingData.find(b => !b.isOnStrike && !b.isOut);
        if (nonStriker) {
          setCurrentNonStriker(nonStriker.playerId);
        }
      }

      // Use Array.isArray to ensure match.innings is an array
      if (Array.isArray(match.innings) && match.innings.length > 0) {
        const currentInnings = match.innings[match.currentInnings - 1];
        if (currentInnings && currentInnings.currentBowlerId) {
          setCurrentBowler(currentInnings.currentBowlerId);
  
        }
      }
    } catch (error) {
      console.error('Error loading match data:', error);
    }
  }, [match.id, match.innings, match.currentInnings]);

  const [showWicketDialog, setShowWicketDialog] = useState(false);
  const [showInningsChangeDialog, setShowInningsChangeDialog] = useState(false);
  const [showTossDialog, setShowTossDialog] = useState(false);
  const [showScoreHistoryDialog, setShowScoreHistoryDialog] = useState(false);
  const [selectedUndoIndex, setSelectedUndoIndex] = useState(-1);
  const [selectedRuns, setSelectedRuns] = useState(0);
  const [selectedBonusBoundary, setSelectedBonusBoundary] = useState(0);
  const [selectedExtras, setSelectedExtras] = useState(0);
  const [selectedExtrasTypes, setSelectedExtrasTypes] = useState<string[]>([]);
  const [extraType, setExtraType] = useState<string>('');
  const [wicketType, setWicketType] = useState<string>('');
  const [runOutPlayerId, setRunOutPlayerId] = useState<string>('');
  const [runOutAssistType, setRunOutAssistType] = useState<string>('');
  const [runOutAssistCount, setRunOutAssistCount] = useState<number>(1);
  const [runOutAssistRoles, setRunOutAssistRoles] = useState<string[]>([]);
  const [currentStriker, setCurrentStriker] = useState<string>('');
  const [currentNonStriker, setCurrentNonStriker] = useState<string>('');
  const [currentBowler, setCurrentBowler] = useState<string>('');

  const [ballDirection, setBallDirection] = useState<string>('');
  const [batsmanStats, setBatsmanStats] = useState<BatsmanStats[]>([]);
  const [bowlerStats, setBowlerStats] = useState<BowlerStats[]>([]);
  const [wickets, setWickets] = useState<Wicket[]>([]);
  const [scoreHistory, setScoreHistory] = useState<ScoreEntry[]>([]);
  const [isFreeHit, setIsFreeHit] = useState(false);
  const [overthrows, setOverthrows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<ScoreEntry | null>(null);
  const [tossWinner, setTossWinner] = useState<string>('');
  const [battingFirst, setBattingFirst] = useState<string>('');
  const [inningsEndReason, setInningsEndReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');
  const [showUndoConfirmation, setShowUndoConfirmation] = useState(false);
  const [isOffsideOpen, setIsOffsideOpen] = useState(false);
  const [isLegsideOpen, setIsLegsideOpen] = useState(false);
  const [isStraightOpen, setIsStraightOpen] = useState(false);
  const [isMiscOpen, setIsMiscOpen] = useState(false);
  const [editingBall, setEditingBall] = useState<ScoreEntry | null>(null);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [shotType, setShotType] = useState<string>('');
  const [ballLength, setBallLength] = useState<string>('');
  const [ballType, setBallType] = useState<string>('');
  const [fieldingPosition, setFieldingPosition] = useState<string>('');
  const [customFieldingPosition, setCustomFieldingPosition] = useState<string>('');
  const [reviewUsed, setReviewUsed] = useState<boolean>(false);
  const [reviewResult, setReviewResult] = useState<'umpires_call' | 'out' | 'not_out' | 'overturned' | 'retained' | 'lost' | null>(null);
  const [runOutAssistFields, setRunOutAssistFields] = useState<{ [key: string]: string }>({});
  const [showMatchCompletionMessage, setShowMatchCompletionMessage] = useState(true); // New state variable
  const [showMatchCompletionDialog, setShowMatchCompletionDialog] = useState(false);
  const [pendingMatchCompletionReason, setPendingMatchCompletionReason] = useState<string>('');

  const [playOverCompleted] = useSound(overCompletedSound, { volume: 0.7 });
  const [umpires, setUmpires] = useState<{ [key: string]: Umpire | null }>({});
  const [playBallHitBat] = useSound(ballHitBatSound, { volume: 0.7 });
  const [liveRoles, setLiveRoles] = useState<{
    striker: { id: string; name: string } | null;
    nonStriker: { id: string; name: string } | null;
    bowler: { id: string; name: string } | null;
    wicketkeeper: { id: string; name: string } | null;
    fielders: Array<{ id: string; name: string; fieldingPosition?: string | null }>;
  }>({ striker: null, nonStriker: null, bowler: null, wicketkeeper: null, fielders: [] });
  const [matchUmpires, setMatchUmpires] = useState<MatchUmpireWithDetails[]>([]);
  const [activeTab, setActiveTab] = useState<string>(initialTab || 'live-scoring');
  // Add state for bowler modal
  const [showBowlerModal, setShowBowlerModal] = useState(false);
  const [nextBowler, setNextBowler] = useState<string | null>(null);

  const team1 = teams.find(t => t.id === match.team1Id);
  const team2 = teams.find(t => t.id === match.team2Id);
  const battingTeam = teams.find(t => t.id === match.currentBattingTeam);
  const bowlingTeam = teams.find(t => t.id === (match.currentBattingTeam === match.team1Id ? match.team2Id : match.team1Id));
  const ballsPerOver = match.ballsPerOver || 6;
  
  const currentScore = match.currentBattingTeam === match.team1Id 
    ? match.team1Score 
    : match.team2Score;

  // Create a list that includes all players but marks dismissed ones as inactive
  const allBatsmenWithStatus = battingTeam?.players?.map(p => {
    // Check if player is dismissed by looking at the wickets table
    const isOut = wickets.some(w => w.dismissedBatterId === p.id);
    return {
      ...p,
      isOut: !!isOut
    };
  }) || [];

  // Available batsmen (only those not dismissed)
  const availableBatsmen = allBatsmenWithStatus.filter(p => !p.isOut);

  const availableBowlers = bowlingTeam?.players || [];


  const RUN_OUT_ASSIST_INFO = {
    direct_hit: {
      count: 1,
      roles: ['Fielder (Direct Hit)'],
      example: 'Run out by Ravindra Jadeja (direct hit)'
    },
    throw_to_keeper: {
      count: 2,
      roles: ['Fielder (Thrower)', 'Wicketkeeper (Breaks stumps)'],
      example: 'Throw by Maxwell, stumps broken by Carey'
    },
    throw_to_bowler: {
      count: 2,
      roles: ['Fielder (Thrower)', 'Bowler (Breaks stumps)'],
      example: 'Throw by du Plessis, run out by Rabada'
    },
    relay_throw: {
      count: 3,
      roles: ['Fielder 1 (Thrower)', 'Fielder 2 (Relay)', 'Keeper/Bowler'],
      example: 'Warner to Smith to Carey'
    },
    keeper_flick: {
      count: 2,
      roles: ['Fielder (Thrower)', 'Wicketkeeper (Flicks ball onto stumps)'],
      example: 'Throw by Pant, flicked onto stumps by Rahul'
    },
    bowler_solo: {
      count: 1,
      roles: ['Bowler (Direct Fielding & Stump Break)'],
      example: 'Bowler Starc fields and runs out batter'
    },
    fielder_solo: {
      count: 1,
      roles: ['Fielder (Fields and breaks stumps by hand)'],
      example: 'Run out by Shami after batter slipped'
    },
    with_obstruction: {
      count: 2,
      roles: ['Fielder (Obstructed/Thrower)', 'Keeper (optional)'],
      example: 'Obstructed throw by Kohli, appeal upheld'
    }
  };

  const RUN_OUT_ASSIST_FIELDS = {
    direct_hit: [
      { id: 'fielder', label: 'Fielder (Direct Hit)' }
    ],
    throw_to_keeper: [
      { id: 'fielder', label: 'Fielder (Thrower)' },
      { id: 'keeper', label: 'Wicketkeeper (Breaks stumps)' }
    ],
    throw_to_bowler: [
      { id: 'fielder', label: 'Fielder (Thrower)' },
      { id: 'bowler', label: 'Bowler (Breaks stumps)' }
    ],
    relay_throw: [
      { id: 'fielder1', label: 'Fielder 1 (Thrower)' },
      { id: 'fielder2', label: 'Fielder 2 (Relay)' },
      { id: 'keeper_bowler', label: 'Keeper/Bowler' }
    ],
    keeper_flick: [
      { id: 'fielder', label: 'Fielder (Thrower)' },
      { id: 'keeper', label: 'Wicketkeeper (Flicks ball onto stumps)' }
    ],
    bowler_solo: [
      { id: 'bowler', label: 'Bowler (Direct Fielding & Stump Break)' }
    ],
    fielder_solo: [
      { id: 'fielder', label: 'Fielder (Fields and breaks stumps by hand)' }
    ],
    with_obstruction: [
      { id: 'fielder', label: 'Fielder (Obstructed/Thrower)' },
      { id: 'keeper', label: 'Keeper (optional)' }
    ]
  };

  const handleRunOutAssistFieldChange = (fieldId: string, value: string) => {
    setRunOutAssistFields(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  useEffect(() => {
    if (runOutAssistType) {
      // Reset fields when assist type changes
      setRunOutAssistFields({});
    }
  }, [runOutAssistType]);

  useEffect(() => {
    if (tossWinner && !battingFirst) {
      setBattingFirst(tossWinner);
    }
  }, [tossWinner, battingFirst]);

  useEffect(() => {
    loadMatchData();
  }, [loadMatchData]);

  useEffect(() => {
    if (batsmanStats.length > 0) {
      saveBattingStats(match.id, batsmanStats).catch(console.error);
    }
  }, [batsmanStats, match.id]);

  useEffect(() => {
    if (bowlerStats.length > 0) {
      saveBowlingStats(match.id, bowlerStats).catch(console.error);
    }
  }, [bowlerStats, match.id]);

  useEffect(() => {
    const umpireIds = [
      (match as any).onFieldUmpire1,
      (match as any).onFieldUmpire2,
      (match as any).thirdUmpire,
      (match as any).fourthUmpire,
    ].filter(Boolean);
    if (umpireIds.length === 0) return;
    (async () => {
      const umpireMap: { [key: string]: Umpire | null } = {};
      for (const id of umpireIds) {
        try {
          umpireMap[id] = await getUmpire(id);
        } catch {
          umpireMap[id] = null;
        }
      }
      setUmpires(umpireMap);
    })();
  }, [match]);

  useEffect(() => {
    async function fetchRoles() {
      if (!match.id || !match.team2Id) return;
      try {
        // team2Id is used as currentBowlingTeamId for now; adjust if needed
        const roles = await fetchLiveMatchRoles(match.id, match.team2Id);
        setLiveRoles(roles);
      } catch (e) {
        // Optionally handle error
      }
    }
    fetchRoles();
  }, [match.id, match.team2Id]);

  useEffect(() => {
    if (match?.id) {
      fetchMatchUmpires(match.id).then(setMatchUmpires).catch(console.error);
    }
  }, [match?.id]);

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  const getUmpireName = (role: string) => {
    const ump = matchUmpires.find((u) => u.role === role);
    return ump ? (ump.umpires?.full_name || ((ump.umpires?.first_name || '') + ' ' + (ump.umpires?.last_name || '')).trim()) : 'N/A';
  };

  const handleStrikerSwap = (newStriker: string, newNonStriker: string) => {
    setCurrentStriker(newStriker);
    setCurrentNonStriker(newNonStriker);
    
    setBatsmanStats(prev => 
      prev.map(b => ({
        ...b,
        isOnStrike: b.playerId === newStriker ? true : b.playerId === newNonStriker ? false : b.isOnStrike
      }))
    );
  };

  const validatePlayers = (): boolean => {
    if (!currentStriker) {
      toast({
        title: "Validation Error",
        description: "Please select a striker before scoring.",
        variant: "destructive",
      });
      return false;
    }
    if (!currentNonStriker) {
      toast({
        title: "Validation Error",
        description: "Please select a non-striker before scoring.",
        variant: "destructive",
      });
      return false;
    }
    if (!currentBowler) {
      toast({
        title: "Validation Error",
        description: "Please select a bowler before scoring.",
        variant: "destructive",
      });
      return false;
    }
    
    // Check if selected players are dismissed
    const strikerIsOut = allBatsmenWithStatus.find(p => p.id === currentStriker)?.isOut;
    const nonStrikerIsOut = allBatsmenWithStatus.find(p => p.id === currentNonStriker)?.isOut;
    
    if (strikerIsOut) {
      toast({
        title: "Validation Error",
        description: "Cannot select a dismissed player as striker.",
        variant: "destructive",
      });
      return false;
    }
    
    if (nonStrikerIsOut) {
      toast({
        title: "Validation Error",
        description: "Cannot select a dismissed player as non-striker.",
        variant: "destructive",
      });
      return false;
    }
    

    if (currentStriker === currentNonStriker) {
      toast({
        title: "Validation Error",
        description: "Striker and non-striker cannot be the same player.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const calculateScoreComponents = (): ScoreComponents => {
    const ballRuns = selectedBonusBoundary > 0 ? selectedBonusBoundary : selectedRuns + selectedBonusBoundary;
    const totalOverthrownRuns = overthrows;
    let teamRuns = 0;
    let batsmanRuns = 0;
    let bowlerRuns = 0;
    let ballCount = 1;

    // Dead ball
    if (selectedExtrasTypes.includes('deadball')) {
      return { teamRuns: 0, batsmanRuns: 0, bowlerRuns: 0, ballCount: 0 };
    }

    // Penalty runs
    if (selectedExtrasTypes.includes('penalty')) {
      teamRuns += 5;
    }

    // No-ball
    if (selectedExtrasTypes.includes('noball')) {
      teamRuns += 1;
      bowlerRuns += 1;
      ballCount = 0;
    }

    // Wide
    if (selectedExtrasTypes.includes('wide')) {
      teamRuns += 1;
      bowlerRuns += 1;
      ballCount = 0;
    }

    // Bye
    if (selectedExtrasTypes.includes('bye')) {
      // Byes are not credited to batsman or bowler
      // If combined with no-ball or wide, ballCount remains 0
      if (!selectedExtrasTypes.includes('noball') && !selectedExtrasTypes.includes('wide')) {
        ballCount = 1;
      }
    }

    // Leg bye
    if (selectedExtrasTypes.includes('legbye')) {
      // Leg byes are not credited to batsman or bowler
      if (!selectedExtrasTypes.includes('noball')) {
        ballCount = 1;
      }
    }

    // Add runs from runs/bonus/overthrows as before
    teamRuns += ballRuns + totalOverthrownRuns;
    batsmanRuns += ballRuns;
    bowlerRuns += ballRuns;

    return { teamRuns, batsmanRuns, bowlerRuns, ballCount };
  };

  const saveInningsData = async () => {
    try {
      const currentInningsData = {
        overNumber: currentScore.overs,
        ballNumber: currentScore.balls,
        runs: currentScore.runs,
        batsmanRuns: 0,
        bowlerRuns: 0,
        extras: 0,
        extraType: '',
        timestamp: Date.now(),
        description: 'Innings data',
        ballCount: 0,
        battingStats: batsmanStats.map(b => ({
          playerId: b.playerId,
          runs: b.runs,
          ballsFaced: b.balls
        })),
        bowlingStats: bowlerStats.map(b => ({
          playerId: b.playerId,
          overs: b.overs,
          runsConceded: b.runsGiven,
          wickets: b.wickets,
          ballsBowled: Math.round(b.overs * ballsPerOver)
        })),
        isFreeHit: false,
        battingTeamId: match.currentBattingTeam,
        bowlingTeamId: match.currentBattingTeam === match.team1Id ? match.team2Id : match.team1Id,
        batsmen: batsmanStats,
        bowlers: bowlerStats,
        currentBowlerId: currentBowler
      };

      // When updating innings data
      const updatedInnings = Array.isArray(match.innings) ? [...match.innings] : [];
      updatedInnings[match.currentInnings - 1] = currentInningsData;

      await updateMatchInnings(match.id, updatedInnings, match.currentBattingTeam, match.currentInnings);
      
      setShowInningsChangeDialog(false);

      console.log('Innings data saved successfully');
    } catch (error) {
      console.error('Error saving innings data:', error);
      toast({
        title: "Error",
        description: "Failed to save innings data",
        variant: "destructive",
      });
    }
  };

  const addToScoreHistory = async (description: string, scoreComponents: ScoreComponents, wicket?: Wicket) => {
    const entry: ScoreEntry = {
      id: uuidv4(), // Generate a UUID for the score entry ID
      runs: selectedRuns, // Only the runs, not the sum
      bonus_boundary: selectedBonusBoundary, // Explicitly set the bonus boundary
      extras: selectedExtras,
      extraType,
      ballCount: scoreComponents.ballCount,
      bowlerRuns: scoreComponents.bowlerRuns,
      batsmanRuns: scoreComponents.batsmanRuns,
      fours: selectedBonusBoundary === 4 ? 1 : 0, // Added for undo functionality
      sixes: selectedBonusBoundary === 6 ? 1 : 0, // Added for undo functionality
      overthrows: overthrows, // Added for undo functionality
      wicket,
      timestamp: Date.now(),
      description,
      ballNumber: currentScore.balls,
      overNumber: currentScore.overs,
      // @ts-expect-error - Property 'innings' is missing from generated types.
      innings: match.currentInnings, // Add current innings number
      battingStats: batsmanStats.map(b => ({
        playerId: b.playerId,
        runs: b.runs,
        ballsFaced: b.balls
      })),
      bowlingStats: bowlerStats.map(b => ({
        playerId: b.playerId,
        overs: b.overs,
        runsConceded: b.runsGiven,
        wickets: b.wickets,
        ballsBowled: Math.round(b.overs * ballsPerOver)
      })),
      isFreeHit,
      bowlerId: currentBowler,
      strikerId: currentStriker,
      nonStrikerId: currentNonStriker,
      ballDirection: ballDirection,
      battingTeamId: match.currentBattingTeam, // Add current batting team ID
    };
    
    setScoreHistory(prev => [...prev, entry]);
    
    await saveScoreHistory(match.id, entry);
  };

  const handleEditEntry = (entry: ScoreEntry) => {
    setEditingEntryId(entry.id || '');
    setEditingEntry({ ...entry });
  };

  const handleSaveEdit = async () => {
    if (!editingEntry || !editingEntryId) return;
    
    try {
      setScoreHistory(prev => 
        prev.map(entry => 
          entry.id === editingEntryId ? editingEntry : entry
        )
      );
      
      await saveScoreHistory(match.id, editingEntry);
      
      setEditingEntryId(null);
      setEditingEntry(null);
      
      toast({
        title: "Entry Updated",
        description: "Score history entry has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating entry:', error);
      toast({
        title: "Error",
        description: "Failed to update score entry",
        variant: "destructive",
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingEntryId(null);
    setEditingEntry(null);
  };

  const handleDeleteSingleEntry = async (entryId: string) => {
    try {
      await deleteScoreHistoryEntry(entryId);
      setScoreHistory(prev => prev.filter(entry => entry.id !== entryId));
      
      toast({
        title: "Entry Deleted",
        description: "Score history entry has been deleted.",
      });
    } catch (error) {
      console.error('Error deleting score entry:', error);
      toast({
        title: "Error",
        description: "Failed to delete score entry",
        variant: "destructive",
      });
    }
  };

  const handleAddScore = async () => {
    playBallHitBat();
    if (!validatePlayers()) return;

    setIsLoading(true);

    const scoreComponents = calculateScoreComponents();
    let newBalls = currentScore.balls + scoreComponents.ballCount;
    let newOvers = currentScore.overs;
    let shouldSwapStrike = false;
    
    if (newBalls >= ballsPerOver) {
      newOvers += 1;
      newBalls = 0;
      shouldSwapStrike = true;
    }

    if ((selectedRuns + selectedBonusBoundary) % 2 === 1 && extraType !== 'wide') {
      shouldSwapStrike = !shouldSwapStrike;
    }

    if (extraType === 'noball') {
      setIsFreeHit(true);
    } else if (isFreeHit && extraType !== 'wide') {
      setIsFreeHit(false);
    }

    if (shouldSwapStrike) {
      const temp = currentStriker;
      setCurrentStriker(currentNonStriker);
      setCurrentNonStriker(temp);
    }
    
    if (currentStriker) {
      setBatsmanStats(prev => {
        const existingIndex = prev.findIndex(b => b.playerId === currentStriker);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            runs: updated[existingIndex].runs + scoreComponents.batsmanRuns,
            balls: updated[existingIndex].balls + scoreComponents.ballCount,
            fours: updated[existingIndex].fours + (selectedBonusBoundary === 4 ? 1 : 0),
            sixes: updated[existingIndex].sixes + (selectedBonusBoundary === 6 ? 1 : 0),
            isOnStrike: shouldSwapStrike ? false : true
          };
          const nonStrikerIndex = updated.findIndex(b => b.playerId === currentNonStriker);
          if (nonStrikerIndex >= 0) {
            updated[nonStrikerIndex].isOnStrike = shouldSwapStrike ? true : false;
          }
          return updated;
        } else {
          return [...prev, {
            playerId: currentStriker,
            runs: scoreComponents.batsmanRuns,
            balls: scoreComponents.ballCount,
            fours: selectedBonusBoundary === 4 ? 1 : 0,
            sixes: selectedBonusBoundary === 6 ? 1 : 0,
            isOut: false,
            isOnStrike: shouldSwapStrike ? false : true
          }];
        }
      });
    }
    
    if (currentBowler) {
      setBowlerStats(prev => {
        const existingIndex = prev.findIndex(b => b.playerId === currentBowler);
        if (existingIndex >= 0) {
          const updated = [...prev];
          const totalBallsBowled = updated[existingIndex].overs * ballsPerOver + scoreComponents.ballCount;
          const oversCompleted = Math.floor(totalBallsBowled / ballsPerOver);
          const ballsInCurrentOver = totalBallsBowled % ballsPerOver;
          const totalOvers = oversCompleted + (ballsInCurrentOver / ballsPerOver);
          
          updated[existingIndex] = {
            ...updated[existingIndex],
            overs: totalOvers,
            runsGiven: updated[existingIndex].runsGiven + scoreComponents.bowlerRuns,
            dots: updated[existingIndex].dots + (scoreComponents.teamRuns === 0 ? 1 : 0),
            economyRate: totalOvers > 0 ? (updated[existingIndex].runsGiven + scoreComponents.bowlerRuns) / totalOvers : 0
          };
          return updated;
        } else {
          const totalOvers = scoreComponents.ballCount / ballsPerOver;
          return [...prev, {
            playerId: currentBowler,
            overs: totalOvers,
            maidens: 0,
            runsGiven: scoreComponents.bowlerRuns,
            wickets: 0,
            dots: scoreComponents.teamRuns === 0 ? 1 : 0,
            economyRate: totalOvers > 0 ? scoreComponents.bowlerRuns / totalOvers : 0,
            average: 0,
            strikeRate: 0
          }];
        }
      });
    }
    
    const description = `${scoreComponents.teamRuns} run${scoreComponents.teamRuns !== 1 ? 's' : ''}${extraType ? ` (${extraType})` : ''}${isFreeHit ? ' [Free Hit]' : ''}`;
    await addToScoreHistory(description, scoreComponents);
    
    await onScoreUpdate(
      match.id,
      match.currentBattingTeam,
      currentScore.runs + scoreComponents.teamRuns,
      currentScore.wickets,
      newOvers,
      newBalls
    );

    // Update innings stats in real-time
    await updateInningsStats(match.id, match.currentInnings, {
      totalRuns: currentScore.runs + scoreComponents.teamRuns,
      totalWickets: currentScore.wickets,
      totalOvers: newOvers + (newBalls / ballsPerOver),
      extrasRuns: await calculateExtrasFromScoreHistory(match.id, match.currentInnings),
      runRate: calculateRunRate(currentScore.runs + scoreComponents.teamRuns, newOvers, newBalls)
    });

    await saveInningsData();
    
    setSelectedRuns(0);
    setSelectedBonusBoundary(0);
    setSelectedExtras(0);
    setExtraType('');
    setOverthrows(0);
    setBallDirection(''); // Reset ball direction
    
    toast({
      title: `${scoreComponents.teamRuns} run${scoreComponents.teamRuns !== 1 ? 's' : ''} scored`,
      description: `Score: ${currentScore.runs + scoreComponents.teamRuns}/${currentScore.wickets}`,
    });

    // Check if match should be automatically completed
    const shouldCompleteMatch = () => {
      // If it's the second innings
      if (match.currentInnings === 2) {
        const previousScore = match.currentBattingTeam === match.team1Id ? match.team2Score : match.team1Score;
        const target = previousScore.runs + 1;
        
        // Check if target is achieved
        if (currentScore.runs + scoreComponents.teamRuns >= target) {
          return true;
        }
        
        // Check if overs completed or wickets completed
        if (newOvers >= (match.totalOvers || 20) || currentScore.wickets >= (match.totalWickets || 10)) {
          return true;
        }
      }
      
      return false;
    };

    if (shouldCompleteMatch()) {
      // Auto-select the reason based on the condition
      if (newOvers >= (match.totalOvers || 20)) {
        setInningsEndReason('overs_completed');
      } else if (currentScore.wickets >= (match.totalWickets || 10)) {
        setInningsEndReason('wickets_completed');
      } else {
        setInningsEndReason('target_achieved');
      }
      
      // Show confirmation dialog before completing match
      setShowMatchCompletionDialog(true);
      setPendingMatchCompletionReason(inningsEndReason);
      return; // Don't proceed with completion until confirmed
    } else if (newOvers >= (match.totalOvers || 20) || currentScore.wickets >= (match.totalWickets || 10)) {
      if (match.currentInnings < (match.totalInnings || 2)) {
        // Auto-select the reason based on the condition
        if (newOvers >= (match.totalOvers || 20)) {
          setInningsEndReason('overs_completed');
        } else if (currentScore.wickets >= (match.totalWickets || 10)) {
          setInningsEndReason('wickets_completed');
        }
        setShowInningsChangeDialog(true);
      } else {
        // Show confirmation dialog before completing match
        setShowMatchCompletionDialog(true);
        setPendingMatchCompletionReason('overs_completed');
        return; // Don't proceed with completion until confirmed
      }
    }

    // Only show bowler modal if this is not the final over of the innings
    if (newBalls === 0 && newOvers > currentScore.overs) {
      const isFinalOver = newOvers >= (match.totalOvers || 20);
      const isInningsEnding = isFinalOver || currentScore.wickets >= (match.totalWickets || 10);
      
      if (!isInningsEnding) {
        setCurrentBowler('');
        setShowBowlerModal(true); // Show modal to select next bowler
        playOverCompleted();
        toast({
          title: "End of Over",
          description: `Over ${newOvers} completed. Please select new bowler.`,
        });
      } else {
        // Final over or innings ending - just play sound without showing modal
        playOverCompleted();
        if (isFinalOver) {
          toast({
            title: "Final Over Completed",
            description: `Over ${newOvers} completed. Innings will end.`,
          });
        } else {
          toast({
            title: "Over Completed",
            description: `Over ${newOvers} completed. Innings ending due to wickets.`,
          });
        }
      }
    }
    setIsLoading(false);
  };

  const handleWicket = async () => {
    if (!validatePlayers()) return;
    setIsLoading(true);
    try {
      const dismissedPlayerId = wicketType === 'run_out' ? runOutPlayerId : currentStriker;
      if (!dismissedPlayerId || !currentBowler) {
        throw new Error('Missing required player information');
      }
      // Only use allowed wicket types
      const allowedWicketTypes = [
        'bowled','caught','lbw','stumped','run_out','hit_wicket','caught_and_bowled','caught_behind','obstructing','handled_ball','timed_out','retired_out'
      ];
      if (!allowedWicketTypes.includes(wicketType)) {
        throw new Error('Invalid wicket type');
      }

      // Check if review was used and result is "not_out"
      const isNotOutReview = reviewUsed && reviewResult === 'not_out';
      
      const wicket = {
        id: uuidv4(),
        matchId: match.id,
        playerId: dismissedPlayerId,
        bowlerId: currentBowler,
        wicketType,
        innings: match.currentInnings,
        overNumber: currentScore.overs,
        ballNumber: currentScore.balls,
        runsScored: currentScore.runs,
        dismissedBatterId: dismissedPlayerId,
        reviewUsed,
        reviewResult,
        // userId is set by Supabase backend/session
      };

      // Calculate new overs and balls (always count as a ball)
      const newBalls = currentScore.balls + 1;
      const newOvers = Math.floor(newBalls / ballsPerOver);
      const remainingBalls = newBalls % ballsPerOver;
      
      if (isNotOutReview) {
        // For "Not Out" reviews, don't create wicket record, just add to score history
        const scoreComponents: ScoreComponents = {
          teamRuns: 0,
          batsmanRuns: 0,
          bowlerRuns: 0,
          ballCount: 1
        };
        
        // Add review event to score history (without wicket)
        const description = `Review - ${wicketType} - ${allBatsmenWithStatus.find(p => p.id === dismissedPlayerId)?.name} - NOT OUT`;
        await addToScoreHistory(description, scoreComponents);
        
        // Update match score (no wicket added)
        await onScoreUpdate(
          match.id,
          match.currentBattingTeam,
          currentScore.runs,
          currentScore.wickets, // No wicket added
          newOvers,
          remainingBalls
        );
        
        toast({
          title: "Review - Not Out",
          description: `${wicketType} appeal - ${allBatsmenWithStatus.find(p => p.id === dismissedPlayerId)?.name} - NOT OUT`,
        });
      } else {
        // Normal wicket recording
        await createWicket(wicket);
        
        // Refresh wickets data to update the UI
        const updatedWickets = await fetchWickets(match.id);
        setWickets(updatedWickets);
        
        // Clear dismissed player from current selections
        if (currentStriker === dismissedPlayerId) {
          setCurrentStriker('');
        }
        if (currentNonStriker === dismissedPlayerId) {
          setCurrentNonStriker('');
        }
        
        // Calculate new score components for wicket
        const newWickets = currentScore.wickets + 1;
        const scoreComponents: ScoreComponents = {
          teamRuns: 0, // No runs scored on wicket
          batsmanRuns: 0,
          bowlerRuns: 0,
          ballCount: 1, // Wicket counts as a ball
          teamWickets: newWickets,
          teamOvers: currentScore.overs,
          teamBalls: currentScore.balls
        };
        
        // Add wicket to score history
        const description = `Wicket - ${wicketType} - ${allBatsmenWithStatus.find(p => p.id === dismissedPlayerId)?.name}`;
        await addToScoreHistory(description, scoreComponents, wicket);
        
        // Update match score
        await onScoreUpdate(
          match.id,
          match.currentBattingTeam,
          currentScore.runs,
          newWickets,
          newOvers,
          remainingBalls
        );
        
        // Update bowler stats
        if (currentBowler) {
          setBowlerStats(prev => {
            const existingIndex = prev.findIndex(b => b.playerId === currentBowler);
            if (existingIndex >= 0) {
              const updated = [...prev];
              const totalBallsBowled = updated[existingIndex].overs * ballsPerOver + 1; // +1 for wicket ball
              const oversCompleted = Math.floor(totalBallsBowled / ballsPerOver);
              const ballsInCurrentOver = totalBallsBowled % ballsPerOver;
              const totalOvers = oversCompleted + (ballsInCurrentOver / ballsPerOver);
              
              updated[existingIndex] = {
                ...updated[existingIndex],
                overs: totalOvers,
                wickets: updated[existingIndex].wickets + 1,
                economyRate: totalOvers > 0 ? updated[existingIndex].runsGiven / totalOvers : 0
              };
              return updated;
            } else {
              const totalOvers = 1 / ballsPerOver;
              return [...prev, {
                playerId: currentBowler,
                overs: totalOvers,
                maidens: 0,
                runsGiven: 0,
                wickets: 1,
                dots: 0,
                economyRate: 0,
                average: 0,
                strikeRate: 0
              }];
            }
          });
        }
        
        // Update innings stats
        await updateInningsStats(match.id, match.currentInnings, {
          totalRuns: currentScore.runs,
          totalWickets: newWickets,
          totalOvers: newOvers + (remainingBalls / ballsPerOver),
          extrasRuns: await calculateExtrasFromScoreHistory(match.id, match.currentInnings),
          runRate: calculateRunRate(currentScore.runs, newOvers, remainingBalls)
        });
        
        toast({
          title: "Wicket Recorded",
          description: `${wicketType} - ${allBatsmenWithStatus.find(p => p.id === dismissedPlayerId)?.name}`,
        });
        
        // Check if match should be automatically completed
        const shouldCompleteMatch = () => {
          // If it's the second innings
          if (match.currentInnings === 2) {
            const previousScore = match.currentBattingTeam === match.team1Id ? match.team2Score : match.team1Score;
            const target = previousScore.runs + 1;
            
            // Check if target is achieved (unlikely with wicket, but possible)
            if (currentScore.runs >= target) {
              return true;
            }
            
            // Check if wickets completed
            if (newWickets >= (match.totalWickets || 10)) {
              return true;
            }
          }
          
          return false;
        };

        if (shouldCompleteMatch()) {
          // Auto-select the reason based on the condition
          if (newWickets >= (match.totalWickets || 10)) {
            setInningsEndReason('wickets_completed');
          } else {
            setInningsEndReason('target_achieved');
          }
          
          // Show confirmation dialog before completing match
          setShowMatchCompletionDialog(true);
          setPendingMatchCompletionReason(inningsEndReason);
          return; // Don't proceed with completion until confirmed
        } else if (newWickets >= (match.totalWickets || 10)) {
          if (match.currentInnings < (match.totalInnings || 2)) {
            setInningsEndReason('wickets_completed');
            setShowInningsChangeDialog(true);
          } else {
            // Show confirmation dialog before completing match
            setShowMatchCompletionDialog(true);
            setPendingMatchCompletionReason('wickets_completed');
            return; // Don't proceed with completion until confirmed
          }
        }
      }
      
      await saveInningsData();
      
      // Reset wicket dialog state
      setWicketType('');
      setRunOutPlayerId('');
      setRunOutAssistType('');
      setRunOutAssistCount(1);
      setRunOutAssistRoles([]);
      setRunOutAssistFields({});
      setReviewUsed(false);
      setReviewResult(null);
      
    } catch (error) {
      console.error('Error recording wicket:', error);
      toast({
        title: 'Error',
        description: 'Failed to record wicket',
        variant: 'destructive',
      });
    } finally {
      setShowWicketDialog(false);
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: 'upcoming' | 'live' | 'completed') => {
    if (newStatus === 'live' && match.status === 'upcoming') {
      setShowTossDialog(true);
      return;
    }
    
    // Show confirmation dialog for match completion
    if (newStatus === 'completed') {
      setShowMatchCompletionDialog(true);
      setPendingMatchCompletionReason('manual');
      return;
    }
    
    await onMatchStatusChange(match.id, newStatus);
    toast({
      title: "Match Status Updated",
      description: `Match is now ${newStatus}`,
    });
  };

  const handleMatchCompletionConfirmed = async () => {
    try {
      // Complete the match
      await onMatchStatusChange(match.id, 'completed');
      
      // Update the innings record if this is the second innings
      if (match.currentInnings === 2) {
        try {
          const innings = await fetchInnings(match.id);
          const secondInning = innings.find(inn => inn.inningsNumber === 2);
          
          if (secondInning) {
            const currentScore = match.currentBattingTeam === match.team1Id ? match.team1Score : match.team2Score;
            const totalOvers = currentScore.overs + (currentScore.balls / (match.ballsPerOver || 6));
            const runRate = calculateRunRate(currentScore.runs, currentScore.overs, currentScore.balls);
            const extrasRuns = await calculateExtrasFromScoreHistory(match.id, 2);
            
            await updateInnings(secondInning.id, {
              endTime: new Date().toISOString(),
              endReason: pendingMatchCompletionReason,
              inningStatus: 'Completed',
              totalRuns: currentScore.runs,
              totalWickets: currentScore.wickets,
              totalOvers: totalOvers,
              extrasRuns: extrasRuns,
              runRate: runRate,
              note: `Match completed: ${pendingMatchCompletionReason === 'manual' ? 'Manually completed' : pendingMatchCompletionReason}`
            });
          }
        } catch (error) {
          console.error('Error updating second innings:', error);
        }
      }
      
      toast({
        title: "Match Completed",
        description: "Match has been completed successfully. All statistics are now finalized.",
      });
      
      // Close the dialog
      setShowMatchCompletionDialog(false);
      setPendingMatchCompletionReason('');
    } catch (error) {
      console.error('Error completing match:', error);
      toast({
        title: "Error",
        description: "Failed to complete match. Please try again.",
        variant: "destructive",
      });
    }
  };

  const saveTossInfoToDatabase = async (tossWinnerId: string, battingFirstId: string) => {
    try {
      const { error } = await supabase
        .from('matches')
        .update({
          toss_winner_id: tossWinnerId,
          toss_selected_batting_team: battingFirstId
        })
        .eq('id', match.id);

      if (error) {
        console.error('Error saving toss info:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error saving toss info to database:', error);
      toast({
        title: "Error",
        description: "Failed to save toss information",
        variant: "destructive",
      });
    }
  };

  const handleTossComplete = async () => {
    console.log('handleTossComplete called');
    console.log('tossWinner:', tossWinner);
    console.log('battingFirst:', battingFirst);
    

    if (!tossWinner || !battingFirst) {
      toast({
        title: "Missing information",
        description: "Please select toss winner and batting team.",
        variant: "destructive",
      });
      return;
    }

    try {
      // First save toss information
      await saveTossInfoToDatabase(tossWinner, battingFirst);
      
      // Update match status to live
      await onMatchStatusChange(match.id, 'live');
      
      // Update the match with the batting first team
      const { error: updateError } = await supabase
        .from('matches')
        .update({
          current_batting_team_id: battingFirst,
          current_innings: 1,
          status: 'live'
        })
        .eq('id', match.id);

      if (updateError) {
        throw updateError;
      }

      // Create the first inning record in the innings table
      const bowlingTeamId = battingFirst === match.team1Id ? match.team2Id : match.team1Id;
      const battingTeam = teams.find(t => t.id === battingFirst);
      const bowlingTeam = teams.find(t => t.id === bowlingTeamId);
      
      await createInnings({
        matchId: match.id,
        inningsNumber: 1,
        battingTeamId: battingFirst,
        bowlingTeamId: bowlingTeamId,
        startTime: new Date().toISOString(),
        endTime: null,
        // New fields
        battingCaptainId: battingTeam?.players?.find(p => p.isCaptain)?.id,
        bowlingCaptainId: bowlingTeam?.players?.find(p => p.isCaptain)?.id,
        inningStatus: 'In Progress',
        isPowerplayApplied: match.matchType === 'T20' || match.matchType === 'ODI',
        powerplayStartOver: 1,
        powerplayEndOver: match.matchType === 'T20' ? 6 : 10,
        note: `First innings started - ${battingTeam?.name} batting first`
      });

      // Initialize innings data
      const initialInningsData = {
        overNumber: 0,
        ballNumber: 0,
        runs: 0,
        batsmanRuns: 0,
        bowlerRuns: 0,
        extras: 0,
        extraType: '',
        timestamp: Date.now(),
        description: 'First innings started',
        ballCount: 0,
        battingStats: [],
        bowlingStats: [],
        isFreeHit: false,
        battingTeamId: battingFirst,
        bowlingTeamId: battingFirst === match.team1Id ? match.team2Id : match.team1Id,
        batsmen: [],
        bowlers: [],
        currentBowlerId: null
      };

      // Update innings data
      await updateMatchInnings(
        match.id,
        [initialInningsData],
        battingFirst,
        1
      );
      
      // Initialize score for the batting team
      await onScoreUpdate(match.id, battingFirst, 0, 0, 0, 0);
      
      setShowTossDialog(false);
      
      // Force a page reload to update all components with new match state
      window.location.reload();
      
      toast({
        title: "Match Started",
        description: `${teams.find(t => t.id === tossWinner)?.name} won the toss. ${teams.find(t => t.id === battingFirst)?.name} is batting first.`,
      });
    } catch (error) {
      console.error('Error starting match:', error);
      toast({
        title: "Error",
        description: "Failed to start match",
        variant: "destructive",
      });
    }
  };

  const saveInningsEndInfoToDatabase = async (reason: string) => {
    try {
      const { error } = await supabase
        .from('matches')
        .update({
          innings_end_reason: reason
        })
        .eq('id', match.id);

      if (error) {
        console.error('Error saving innings end reason:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error saving innings end reason to database:', error);
      toast({
        title: "Error",
        description: "Failed to save innings end reason",
        variant: "destructive",
      });
    }
  };

  const handleInningsChange = async () => {
    try {
      const reason = inningsEndReason === 'others' ? customReason : inningsEndReason;
      
      if (inningsEndReason === 'others' && !customReason.trim()) {
        toast({
          title: "Validation Error",
          description: "Please provide a custom reason.",
          variant: "destructive",
        });
        return;
      }

      await saveInningsEndInfoToDatabase(reason);

      const newBattingTeam = match.currentBattingTeam === match.team1Id ? match.team2Id : match.team1Id;
      
      // Fetch the first inning record to get its ID
      const innings = await fetchInnings(match.id);
      const firstInning = innings.find(inn => inn.inningsNumber === 1);
      
      if (firstInning) {
        // Calculate current stats
        const currentScore = match.currentBattingTeam === match.team1Id 
          ? match.team1Score 
          : match.team2Score;
        
        const totalOvers = currentScore.overs + (currentScore.balls / ballsPerOver);
        const runRate = calculateRunRate(currentScore.runs, currentScore.overs, currentScore.balls);
        const extrasRuns = await calculateExtrasFromScoreHistory(match.id, 1);
        
        // Update the first inning record with end time and reason
        await updateInnings(firstInning.id, {
          endTime: new Date().toISOString(),
          endReason: reason,
          inningStatus: 'Completed',
          totalRuns: currentScore.runs,
          totalWickets: currentScore.wickets,
          totalOvers: totalOvers,
          extrasRuns: extrasRuns,
          runRate: runRate,
          note: `Innings ended: ${INNINGS_END_REASON_DISPLAY[reason as keyof typeof INNINGS_END_REASON_DISPLAY]}`
        });
      }

      // Create the second inning record
      const newBattingTeamObj = teams.find(t => t.id === newBattingTeam);
      const newBowlingTeamObj = teams.find(t => t.id === match.currentBattingTeam);
      
      await createInnings({
        matchId: match.id,
        inningsNumber: 2,
        battingTeamId: newBattingTeam,
        bowlingTeamId: match.currentBattingTeam,
        startTime: new Date().toISOString(),
        endTime: null,
        // New fields
        battingCaptainId: newBattingTeamObj?.players?.find(p => p.isCaptain)?.id,
        bowlingCaptainId: newBowlingTeamObj?.players?.find(p => p.isCaptain)?.id,
        inningStatus: 'In Progress',
        isPowerplayApplied: match.matchType === 'T20' || match.matchType === 'ODI',
        powerplayStartOver: 1,
        powerplayEndOver: match.matchType === 'T20' ? 6 : 10,
        note: `Second innings started - ${newBattingTeamObj?.name} batting`
      });
      
      // When updating innings after change
      await updateMatchInnings(match.id, Array.isArray(match.innings) ? match.innings : [], newBattingTeam, 2);
      
      await onScoreUpdate(
        match.id,
        newBattingTeam,
        0,
        0,
        0,
        0
      );
      
      setCurrentStriker('');
      setCurrentNonStriker('');
      setCurrentBowler('');
      
      setBatsmanStats([]);
      setBowlerStats([]);
      setScoreHistory([]);
      setIsFreeHit(false);
      setInningsEndReason('');
      setCustomReason('');
      
      setShowInningsChangeDialog(false);
      
      window.location.reload();
      
      toast({
        title: "Innings Changed",
        description: `${teams.find(t => t.id === newBattingTeam)?.name} is now batting. Reason: ${INNINGS_END_REASON_DISPLAY[reason as keyof typeof INNINGS_END_REASON_DISPLAY]}`,
      });
    } catch (error) {
      console.error('Error changing innings:', error);
      toast({
        title: "Error",
        description: "Failed to change innings",
        variant: "destructive",
      });
    }
  };

  const handleExtraSelect = (type: string) => {
    // Dead Ball is always single
    if (type === 'deadball') {
      setSelectedExtrasTypes(selectedExtrasTypes.includes('deadball') ? [] : ['deadball']);
      setSelectedExtras(0);
      return;
    }

    // Remove deadball if any other is selected
    let newTypes = selectedExtrasTypes.filter(t => t !== 'deadball');

    // Toggle logic
    if (newTypes.includes(type)) {
      newTypes = newTypes.filter(t => t !== type);
    } else {
      // Enforce mutual exclusivity
      if (type === 'noball') newTypes = newTypes.filter(t => t !== 'wide');
      if (type === 'wide') newTypes = newTypes.filter(t => t !== 'noball' && t !== 'legbye');
      if (type === 'bye') newTypes = newTypes.filter(t => t !== 'legbye');
      if (type === 'legbye') newTypes = newTypes.filter(t => t !== 'bye' && t !== 'wide');
      // Add the new type
      newTypes.push(type);
    }

    setSelectedExtrasTypes(newTypes);

    // Set extras value for penalty
    if (type === 'penalty' && !newTypes.includes('penalty')) {
      setSelectedExtras(0);
    } else if (newTypes.includes('penalty')) {
      setSelectedExtras(5);
    } else {
      setSelectedExtras(0);
    }
  };

  const handleOverthrownSelect = (runs: number) => {
    if (overthrows === runs) {
      setOverthrows(0);
    } else {
      setOverthrows(runs);
    }
  };

  const getCurrentPlayerName = (playerId: string) => {
    const allPlayers = [...(team1?.players || []), ...(team2?.players || [])];
    const player = allPlayers.find(p => p.id === playerId);
    return player?.name || 'Unknown';
  };

  const formatBowlingStats = (bowler: BowlerStats) => {
    const completedOvers = Math.floor(bowler.overs);
    const ballsInCurrentOver = Math.round((bowler.overs - completedOvers) * ballsPerOver);
    const oversDisplay = ballsInCurrentOver > 0 ? `${completedOvers}.${ballsInCurrentOver}` : completedOvers.toString();
    
    return {
      overs: oversDisplay,
      maidens: bowler.maidens,
      runs: bowler.runsGiven,
      wickets: bowler.wickets,
      economy: bowler.economyRate.toFixed(1),
      dots: bowler.dots
    };
  };

  const getInningsDisplay = (inningsNumber: number) => {
    const suffixes = ['', 'st', 'nd', 'rd'];
    const suffix = suffixes[inningsNumber] || 'th';
    return `${inningsNumber}${suffix}`;
  };

  const getCurrentInningsTeam = () => {
    return teams.find(t => t.id === match.currentBattingTeam);
  };

  const handleUndo = () => {
    if (scoreHistory.length > 0) {
      const lastEntry = scoreHistory[scoreHistory.length - 1];
      setShowUndoConfirmation(true);

      // Pre-fill dialog with last entry details
      if (lastEntry.fours === 1) {
        setSelectedRuns(0); // If it was a 4, runs input should be 0
        setSelectedBonusBoundary(4);
      } else if (lastEntry.sixes === 1) {
        setSelectedRuns(0); // If it was a 6, runs input should be 0
        setSelectedBonusBoundary(6);
      } else {
        // If not a boundary, set runs normally and reset bonus boundary
        setSelectedRuns(lastEntry.runs || 0);
        setSelectedBonusBoundary(0);
      }
      
      setSelectedExtras(lastEntry.extras);
      setExtraType(lastEntry.extraType);
      setOverthrows(lastEntry.overthrows || 0); // Safely access optional property
      setBallDirection(lastEntry.ballDirection || ''); // Safely access optional property
    }
  };

  const confirmUndo = async () => {
    if (scoreHistory.length === 0) return;

    setIsLoading(true);
    const lastEntry = scoreHistory[scoreHistory.length - 1];

    try {
      // Delete the last score history entry
      if (lastEntry.id) {
        await deleteScoreHistoryEntry(lastEntry.id);
      }

      // If the last entry was a wicket, delete that wicket record
      if (lastEntry.wicket && lastEntry.wicket.id) {
        await deleteWicket(lastEntry.wicket.id);
      }

      // Re-fetch all relevant data to ensure consistency
      await loadMatchData(); // This will re-fetch all match, batting, bowling stats, and score history

      // After loading, recalculate total runs, wickets, overs, and balls
      let recalculatedRuns = 0;
      let recalculatedBallsBowled = 0;
      for (const entry of scoreHistory) {
        recalculatedRuns += entry.runs; // Sum up total runs from history
        recalculatedBallsBowled += entry.ballCount; // Sum up total balls from history
      }
      const recalculatedWickets = wickets.length; // Use the updated wickets array length
      const recalculatedOvers = Math.floor(recalculatedBallsBowled / ballsPerOver);
      const recalculatedBalls = recalculatedBallsBowled % ballsPerOver;

      // Update the parent match state with the recalculated scores
      await onScoreUpdate(
        match.id,
        match.currentBattingTeam,
        recalculatedRuns,
        recalculatedWickets,
        recalculatedOvers,
        recalculatedBalls
      );

      // Reset score input fields
      setSelectedRuns(0);
      setSelectedBonusBoundary(0);
      setSelectedExtras(0);
      setExtraType('');
      setOverthrows(0);
      setBallDirection('');
      setWicketType('');
      setRunOutPlayerId('');

      toast({
        title: "Undo Successful",
        description: "Last score entry has been undone.",
      });
    } catch (error) {
      console.error("Error undoing last entry:", error);
      toast({
        title: "Error",
        description: "Failed to undo last score entry.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setShowUndoConfirmation(false);
    }
  };

  const handleBallClick = (ball: ScoreEntry) => {
    setEditingBall(ball);

    // Set both runs and bonus boundary from the ball fields, fallback to fours/sixes for legacy entries
    setSelectedRuns(ball.runs || 0);
    if (typeof ball.bonus_boundary === 'number' && ball.bonus_boundary > 0) {
      setSelectedBonusBoundary(ball.bonus_boundary);
    } else if (ball.fours === 1) {
      setSelectedBonusBoundary(4);
    } else if (ball.sixes === 1) {
      setSelectedBonusBoundary(6);
    } else {
      setSelectedBonusBoundary(0);
    }

    setSelectedExtras(ball.extras);
    setExtraType(ball.extraType);
    setOverthrows(ball.overthrows || 0);
    setBallDirection(ball.ballDirection || '');
    if (ball.wicket) {
      setWicketType(ball.wicket.wicketType);
      setRunOutPlayerId(ball.wicket.playerId);
    }
  };

  const handleRestoreEntry = (entry: ScoreEntry) => {
    setScoreHistory(prev => [...prev, entry]);
  };

  const handleUpdateScore = async () => {
    if (!editingBall || !validatePlayers()) return;

    setIsLoading(true);

    try {
      // Prepare updated fields
      const scoreComponents = calculateScoreComponents();
      const updatedFields = {
        runs: selectedRuns,
        bonus_boundary: selectedBonusBoundary,
        extras: selectedExtras,
        extraType,
        overthrows,
        ballDirection,
        isFreeHit,
        bowlerRuns: scoreComponents.bowlerRuns,
        batsmanRuns: scoreComponents.batsmanRuns,
        fours: selectedBonusBoundary === 4 ? 1 : 0,
        sixes: selectedBonusBoundary === 6 ? 1 : 0,
        description: `${scoreComponents.teamRuns} run${scoreComponents.teamRuns !== 1 ? 's' : ''}${extraType ? ` (${extraType})` : ''}${isFreeHit ? ' [Free Hit]' : ''} (edited)`
      };

      // Update in local state
      setScoreHistory(prev => prev.map(entry => entry.id === editingBall.id ? { ...entry, ...updatedFields } : entry));

      // Update in DB
      await updateScoreHistoryEntry(editingBall.id, updatedFields);

      // Reset editing state
      setEditingBall(null);
      setSelectedRuns(0);
      setSelectedBonusBoundary(0);
      setSelectedExtras(0);
      setExtraType('');
      setOverthrows(0);
      setBallDirection('');
      setWicketType('');
      setRunOutPlayerId('');

      toast({
        title: "Score Updated",
        description: "Ball data has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating score:', error);
      toast({
        title: "Error",
        description: "Failed to update ball data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (showMatchCompletionMessage && (currentScore.overs >= (match.totalOvers || 20) || currentScore.wickets >= 10)) {
    if (match.status === 'live' && match.currentInnings === 2) {
      return (
        <div className="space-y-4">
          <MatchStatus match={match} teams={teams} />
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="text-orange-800">Match Completion</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>
                  Match appears to be complete. Would you like to mark it as finished?
                </AlertDescription>
              </Alert>
              <div className="flex gap-2 mt-4">
                <Button 
                  onClick={() => {
                    setShowMatchCompletionDialog(true);
                    setPendingMatchCompletionReason('manual');
                  }}
                  variant="destructive"
                >
                  Complete Match
                </Button>
                <Button variant="outline" onClick={() => setShowMatchCompletionMessage(false)}>
                  Continue Editing
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  const getPlayerSortPriority = (player, team, match) => {
    const isCaptain = player.id === (team.id === match.team1Id ? match.team1CaptainId : match.team2CaptainId);
    const isViceCaptain = player.id === (team.id === match.team1Id ? match.team1ViceCaptainId : match.team2ViceCaptainId);
    if (isCaptain) return 0;
    if (isViceCaptain) return 1;
    return 2;
  };

  const currentInningsData = Array.isArray(match.innings) ? match.innings[match.currentInnings - 1] : undefined;
  const strikerId = currentInningsData?.strikerId;
  const nonStrikerId = currentInningsData?.nonStrikerId;

  return (
    <div className="space-y-4">
      <MatchStatus match={match} teams={teams} />
      
      {/* Toss Dialog */}
      <Dialog open={showTossDialog} onOpenChange={setShowTossDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Match Start - Toss Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="tossWinner">Who won the toss?</Label>
              <Select value={tossWinner} onValueChange={setTossWinner}>
                <SelectTrigger>
                  <SelectValue placeholder="Select toss winner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={match.team1Id}>{teams.find(t => t.id === match.team1Id)?.name}</SelectItem>
                  <SelectItem value={match.team2Id}>{teams.find(t => t.id === match.team2Id)?.name}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="battingFirst">Who is batting first?</Label>
              <Select value={battingFirst} onValueChange={setBattingFirst}>
                <SelectTrigger>
                  <SelectValue placeholder="Select batting team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={match.team1Id}>{teams.find(t => t.id === match.team1Id)?.name}</SelectItem>
                  <SelectItem value={match.team2Id}>{teams.find(t => t.id === match.team2Id)?.name}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button onClick={handleTossComplete} className="w-full">
              Start Match
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Undo Confirmation Dialog */}
      <Dialog open={showUndoConfirmation} onOpenChange={setShowUndoConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Undo</DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertDescription>
              Are you sure you want to undo the last recorded ball? This will permanently remove the score and any associated wicket from the history.
            </AlertDescription>
          </Alert>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowUndoConfirmation(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmUndo} disabled={isLoading}>
              Confirm Undo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Innings Change Dialog */}
      <Dialog open={showInningsChangeDialog} onOpenChange={setShowInningsChangeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End Current Innings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reason for ending innings</Label>
              <Select value={inningsEndReason} onValueChange={setInningsEndReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {INNINGS_END_REASONS.map((reason) => (
                    <SelectItem key={reason} value={reason}>
                      {INNINGS_END_REASON_DISPLAY[reason]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {inningsEndReason === 'others' && (
              <div className="space-y-2">
                <Label>Custom Reason</Label>
                <Input
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter custom reason"
                />
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowInningsChangeDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleInningsChange} disabled={isLoading}>
              End This Inning
            </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 overflow-x-auto">
          <TabsTrigger value="live-scoring" className="text-xs sm:text-sm">Live Scoring</TabsTrigger>
          <TabsTrigger value="batting-stats" className="text-xs sm:text-sm">Batting Stats</TabsTrigger>
          <TabsTrigger value="bowling-stats" className="text-xs sm:text-sm">Bowling Stats</TabsTrigger>
          <TabsTrigger value="score-history" className="text-xs sm:text-sm">Score History</TabsTrigger>
          <TabsTrigger value="match-info" className="text-xs sm:text-sm">Match Info</TabsTrigger>
        </TabsList>
        <TabsContent value="live-scoring">
          {/* Main content of the scoreboard for live scoring */}
          <div className={`grid grid-cols-1 ${isAdvancedMode ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-4 transition-all duration-300`}>
            {/* Left Column - Live Scoreboard, Current Players, Match Overview */}
            <div className={`space-y-4 transition-all duration-300 ${isAdvancedMode ? 'order-2' : 'order-1'}`}>
              <Collapsible defaultOpen className="group">
                <Card className="bg-card">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                      <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        Live Scoreboard
                        <div className="flex gap-1">
                          {match.status === 'live' && (
                            <Badge className="bg-green-500 animate-pulse text-xs">
                              <div className="w-2 h-2 bg-foreground rounded-full mr-1"></div>
                              LIVE
                            </Badge>
                          )}
                          {isFreeHit && (
                            <Badge className="bg-orange-500 text-xs">FREE HIT</Badge>
                          )}
                        </div>
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Select value={match.status} onValueChange={handleStatusChange}>
                          <SelectTrigger className="w-24 sm:w-32 text-xs sm:text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="upcoming" disabled={match.status === 'live' || match.status === 'completed'}>Upcoming</SelectItem>
                            <SelectItem value="live" disabled={match.status === 'completed'}>Live</SelectItem>
                            <SelectItem value="completed" disabled={match.status === 'upcoming'}>Completed</SelectItem>
                          </SelectContent>
                        </Select>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="w-9 p-0 border border-input">
                            <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                            <span className="sr-only">Toggle</span>
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="text-center mb-4 bg-card dark:bg-card">
                      <div className="text-2xl sm:text-3xl font-bold text-foreground">
                          {currentScore.runs}/{currentScore.wickets}
                        </div>
                      <div className="text-base sm:text-lg text-muted-foreground">
                          {currentScore.overs}.{currentScore.balls} overs
                        </div>
                      <div className="text-xs sm:text-sm text-muted-foreground">
                          {battingTeam?.name} batting (Innings {getInningsDisplay(match.currentInnings)})
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              <Collapsible defaultOpen className="group">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Current Players</CardTitle>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="w-9 p-0 border border-input">
                        <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                        <span className="sr-only">Toggle</span>
                      </Button>
                    </CollapsibleTrigger>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Batting Team */}
                        <div className="mb-2 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                          <h4 className="font-semibold text-sm sm:text-base">Batting Team Players ({battingTeam?.name})</h4>
                          <StrikerSwapButton
                            currentStriker={currentStriker}
                            currentNonStriker={currentNonStriker}
                            onSwap={handleStrikerSwap}
                            getPlayerName={getCurrentPlayerName}
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs sm:text-sm font-medium">Striker ({battingTeam?.name})</label>
                            <Select value={currentStriker} onValueChange={setCurrentStriker}>
                              <SelectTrigger className="text-xs sm:text-sm">
                                <SelectValue placeholder="Select striker" />
                              </SelectTrigger>
                              <SelectContent>
                            {allBatsmenWithStatus
                              .filter(player => player.id !== currentNonStriker)
                              .sort((a, b) => {
                                // Sort: active players first, then dismissed players
                                if (a.isOut && !b.isOut) return 1;
                                if (!a.isOut && b.isOut) return -1;
                                return 0;
                              })
                              .map(player => (
                                  <SelectItem 
                                    key={player.id} 
                                    value={player.id}
                                    disabled={player.isOut}
                                    className={player.isOut ? "opacity-50 cursor-not-allowed" : ""}
                                  >
                                    <span className={player.isOut ? "line-through text-gray-500" : ""}>
                                      {player.name}
                                    </span>
                                    {player.isOut && (
                                      <span className="ml-2 text-xs text-red-500">(OUT)</span>
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {currentStriker && (
                              <p className="text-xs text-green-600 mt-1">
                                On Strike: {getCurrentPlayerName(currentStriker)}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-xs sm:text-sm font-medium">Non-Striker ({battingTeam?.name})</label>
                            <Select value={currentNonStriker} onValueChange={setCurrentNonStriker}>
                              <SelectTrigger className="text-xs sm:text-sm">
                                <SelectValue placeholder="Select non-striker" />
                              </SelectTrigger>
                              <SelectContent>
                            {allBatsmenWithStatus
                              .filter(player => player.id !== currentStriker)
                              .sort((a, b) => {
                                // Sort: active players first, then dismissed players
                                if (a.isOut && !b.isOut) return 1;
                                if (!a.isOut && b.isOut) return -1;
                                return 0;
                              })
                              .map(player => (
                                  <SelectItem 
                                    key={player.id} 
                                    value={player.id}
                                    disabled={player.isOut}
                                    className={player.isOut ? "opacity-50 cursor-not-allowed" : ""}
                                  >
                                    <span className={player.isOut ? "line-through text-gray-500" : ""}>
                                      {player.name}
                                    </span>
                                    {player.isOut && (
                                      <span className="ml-2 text-xs text-red-500">(OUT)</span>
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="border-t pt-4 mt-4">
                          <h4 className="font-semibold mb-1 text-sm sm:text-base">Bowling Team Players ({bowlingTeam?.name})</h4>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs sm:text-sm font-medium">Current Bowler</label>
                            <Select
                              value={currentBowler}
                              onValueChange={async (value) => {
                                setCurrentBowler(value);
                                const currentInningsData = (Array.isArray(match.innings) ? match.innings : [])[match.currentInnings - 1];
                                if (currentInningsData) {
                                  await updateMatchInnings(
                                    match.id,
                                    (Array.isArray(match.innings) ? match.innings : []).map((inn, index) => {
                                      if (index === match.currentInnings - 1) {
                                        return { ...inn, currentBowlerId: value };
                                      }
                                      return inn;
                                    }),
                                    match.currentBattingTeam,
                                    match.currentInnings
                                  );
                                }
                              }}
                            >
                              <SelectTrigger className="w-full sm:w-[180px] text-xs sm:text-sm">
                                <SelectValue placeholder="Select Bowler" />
                            </SelectTrigger>
                            <SelectContent>
                            {availableBowlers
                              .map(player => (
                                <SelectItem key={player.id} value={player.id}>
                                  {player.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          </div>


                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              <MatchOverviewPanel
                scoreHistory={scoreHistory}
                teams={teams}
                currentInnings={match.currentInnings}
                currentBattingTeam={match.currentBattingTeam}
                onBallClick={handleBallClick}
                totalOvers={match.totalOvers || 20}
                ballsPerOver={match.ballsPerOver || 6}
              />

              <CricketGroundPanel />

              <Card className="bg-card">
                <CardHeader>
                  <CardTitle>Scorecard</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className={`border rounded-lg p-4 transition-all duration-300 ${match.currentBattingTeam === team1?.id ? 'border-green-300 bg-muted animate' : 'border-gray-200 bg-muted'}`}>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        {team1?.name}
                        {team1?.color && (
                          <div 
                            className="w-4 h-4 rounded border"
                            style={{ backgroundColor: team1.color }}
                          />
                        )}
                        {match.currentBattingTeam === team1?.id && (
                          <Badge className="bg-green-500">Batting</Badge>
                        )}
                      </h3>
                      <div className="flex justify-between">
                        <span className="text-xl font-bold">
                          {match.team1Score.runs}/{match.team1Score.wickets}
                        </span>
                        <span>
                          {match.team1Score.overs}.{match.team1Score.balls} overs
                        </span>
                      </div>
                    </div>
                    
                    <div className={`border rounded-lg p-4 transition-all duration-300 ${match.currentBattingTeam === team2?.id ? 'border-green-300 bg-muted animate' : 'border-gray-200 bg-muted'}`}>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        {team2?.name}
                        {team2?.color && (
                          <div 
                            className="w-4 h-4 rounded border"
                            style={{ backgroundColor: team2.color }}
                          />
                        )}
                        {match.currentBattingTeam === team2?.id && (
                          <Badge className="bg-green-500">Batting</Badge>
                        )}
                      </h3>
                      <div className="flex justify-between">
                        <span className="text-xl font-bold">
                          {match.team2Score.runs}/{match.team2Score.wickets}
                        </span>
                        <span>
                          {match.team2Score.overs}.{match.team2Score.balls} overs
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Score Input */}
            {match.status === 'live' && (
              <div className={`space-y-4 transition-all duration-300 ${isAdvancedMode ? 'order-1' : 'order-2'}`}>
                <Card className={`relative transition-all duration-300 ${isAdvancedMode ? 'min-h-[80vh]' : ''}`}>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Score Input</CardTitle>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setIsAdvancedMode(!isAdvancedMode)}
                          className="ml-2"
                        >
                          {isAdvancedMode ? 'Simple Mode' : 'Advanced Mode'}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={handleUndo} 
                          disabled={isLoading || scoreHistory.length === 0} 
                          className="border border-gray-300"
                        >
                          <Undo className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className={`space-y-6 transition-all duration-300 ${isAdvancedMode ? 'h-full' : ''}`}>
                    <div className={`${isAdvancedMode ? 'grid grid-cols-2 gap-6' : ''}`}>
                      {/* Left side - Current buttons and functions */}
                      <div className="space-y-6">
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold">Runs</h4>
                            {selectedRuns >= 0 && (
                              <p className="text-xs text-blue-600">Selected: {selectedRuns} runs</p>
                            )}
                          </div>
                          <TooltipProvider>
                            <div className="grid grid-cols-5 gap-2">
                              {[0, 1, 2, 3, 4].map((run) => (
                                <Tooltip key={run}>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      onClick={() => {
                                        setSelectedRuns(selectedRuns === run ? 0 : run);
                                      }}
                                      variant={selectedRuns === run ? "default" : "outline"}
                                      className={selectedRuns === run ? "bg-blue-600" : ""}
                                      disabled={isLoading || selectedBonusBoundary > 0}
                                    >
                                      {run}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{run === 0 ? 'Dot ball (no runs)' : `${run} run${run > 1 ? 's' : ''} scored`}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ))}
                            </div>
                          </TooltipProvider>
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold">Bonus Boundary</h4>
                            {selectedBonusBoundary > 0 && (
                              <p className="text-xs text-green-600">Boundary: {selectedBonusBoundary}</p>
                            )}
                          </div>
                          <TooltipProvider>
                            <div className="grid grid-cols-2 gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    onClick={() => {
                                      setSelectedBonusBoundary(selectedBonusBoundary === 4 ? 0 : 4);
                                    }}
                                    variant={selectedBonusBoundary === 4 ? "default" : "outline"}
                                    className={selectedBonusBoundary === 4 ? "bg-blue-600" : ""}
                                    disabled={isLoading}
                                  >
                                    4
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Four runs (boundary)</p>
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    onClick={() => {
                                      setSelectedBonusBoundary(selectedBonusBoundary === 6 ? 0 : 6);
                                    }}
                                    variant={selectedBonusBoundary === 6 ? "default" : "outline"}
                                    className={selectedBonusBoundary === 6 ? "bg-green-600" : ""}
                                    disabled={isLoading}
                                  >
                                    6
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Six runs (boundary)</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold">Extras</h4>
                            {selectedExtrasTypes.length > 0 && (
                              <p className="text-xs text-orange-600">
                                Extras: {selectedExtrasTypes.join(', ')}
                                {selectedExtras > 0 && ` (+${selectedExtras})`}
                              </p>
                            )}
                          </div>
                          <TooltipProvider>
                            <div className="grid grid-cols-2 gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    onClick={() => handleExtraSelect('wide')}
                                    variant={selectedExtrasTypes.includes('wide') ? "default" : "outline"}
                                    className={selectedExtrasTypes.includes('wide') ? "bg-orange-600" : ""}
                                    disabled={isLoading}
                                  >
                                    Wide (+1)
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Wide ball - too wide for batsman to reach</p>
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    onClick={() => handleExtraSelect('noball')}
                                    variant={selectedExtrasTypes.includes('noball') ? "default" : "outline"}
                                    className={selectedExtrasTypes.includes('noball') ? "bg-orange-600" : ""}
                                    disabled={isLoading}
                                  >
                                    No Ball (+1)
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>No ball - illegal delivery, next ball is free hit</p>
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    onClick={() => handleExtraSelect('bye')}
                                    variant={selectedExtrasTypes.includes('bye') ? "default" : "outline"}
                                    className={selectedExtrasTypes.includes('bye') ? "bg-orange-600" : ""}
                                    disabled={isLoading}
                                  >
                                    Bye
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Bye - ball passes batsman without contact</p>
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    onClick={() => handleExtraSelect('legbye')}
                                    variant={selectedExtrasTypes.includes('legbye') ? "default" : "outline"}
                                    className={selectedExtrasTypes.includes('legbye') ? "bg-orange-600" : ""}
                                    disabled={isLoading}
                                  >
                                    Leg Bye
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Leg bye - ball hits batsman's body</p>
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    onClick={() => handleExtraSelect('penalty')}
                                    variant={selectedExtrasTypes.includes('penalty') ? "default" : "outline"}
                                    className={selectedExtrasTypes.includes('penalty') ? "bg-red-600" : ""}
                                    disabled={isLoading}
                                  >
                                    Penalty (+5)
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>5 runs awarded by umpire for fielding or conduct violation</p>
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    onClick={() => handleExtraSelect('deadball')}
                                    variant={selectedExtrasTypes.includes('deadball') ? "default" : "outline"}
                                    className={selectedExtrasTypes.includes('deadball') ? "bg-gray-600 text-white" : ""}
                                    disabled={isLoading}
                                  >
                                    Dead Ball
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>No runs, not a legal delivery. Ball is dead.</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold">Overthrows</h4>
                            {overthrows > 0 && (
                              <p className="text-xs text-purple-600">Overthrows: +{overthrows}</p>
                            )}
                          </div>
                          <TooltipProvider>
                            <div className="grid grid-cols-5 gap-2">
                              {[0, 1, 2, 3, 4].map((runs) => (
                                <Tooltip key={runs}>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      onClick={() => handleOverthrownSelect(runs)}
                                      variant={overthrows === runs ? "default" : "outline"}
                                      className={overthrows === runs ? "bg-purple-600" : ""}
                                      disabled={isLoading}
                                    >
                                      {runs}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{runs === 0 ? 'No overthrows' : `${runs} overthrow run${runs > 1 ? 's' : ''}`}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ))}
                            </div>
                          </TooltipProvider>
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold">Ball Direction</h4>
                            {ballDirection && (
                              <p className="text-xs text-blue-600">Selected: {ballDirection.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</p>
                            )}
                          </div>
                          <Select value={ballDirection} onValueChange={setBallDirection} disabled={isLoading}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select ball direction" />
                            </SelectTrigger>
                            <SelectContent>
                              <Collapsible open={isOffsideOpen} onOpenChange={setIsOffsideOpen}>
                                <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                  <span>Off-side (Right-hand batter's left)</span>
                                  <ChevronDown className={`h-4 w-4 transition-transform ${isOffsideOpen ? 'rotate-180' : ''}`} />
                                </CollapsibleTrigger>
                                <CollapsibleContent className="pl-4 pr-2 pb-2">
                                  <SelectGroup>
                                    <SelectItem value="third_man">Third Man</SelectItem>
                                    <SelectItem value="slip">Slip</SelectItem>
                                    <SelectItem value="gully">Gully</SelectItem>
                                    <SelectItem value="point">Point</SelectItem>
                                    <SelectItem value="cover">Cover</SelectItem>
                                    <SelectItem value="extra_cover">Extra Cover</SelectItem>
                                    <SelectItem value="mid_off">Mid Off</SelectItem>
                                    <SelectItem value="deep_point">Deep Point</SelectItem>
                                    <SelectItem value="deep_cover">Deep Cover</SelectItem>
                                    <SelectItem value="deep_extra_cover">Deep Extra Cover</SelectItem>
                                    <SelectItem value="long_off">Long Off</SelectItem>
                                  </SelectGroup>
                                </CollapsibleContent>
                              </Collapsible>
                              
                              <Collapsible open={isLegsideOpen} onOpenChange={setIsLegsideOpen}>
                                <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                  <span>Leg-side / On-side (Right-hand batter's right)</span>
                                  <ChevronDown className={`h-4 w-4 transition-transform ${isLegsideOpen ? 'rotate-180' : ''}`} />
                                </CollapsibleTrigger>
                                <CollapsibleContent className="pl-4 pr-2 pb-2">
                                  <SelectGroup>
                                    <SelectItem value="fine_leg">Fine Leg</SelectItem>
                                    <SelectItem value="square_leg">Square Leg</SelectItem>
                                    <SelectItem value="mid_wicket">Mid Wicket</SelectItem>
                                    <SelectItem value="deep_square_leg">Deep Square Leg</SelectItem>
                                    <SelectItem value="deep_mid_wicket">Deep Mid Wicket</SelectItem>
                                    <SelectItem value="long_on">Long On</SelectItem>
                                    <SelectItem value="short_fine_leg">Short Fine Leg</SelectItem>
                                    <SelectItem value="short_square_leg">Short Square Leg</SelectItem>
                                    <SelectItem value="short_mid_wicket">Short Mid Wicket</SelectItem>
                                  </SelectGroup>
                                </CollapsibleContent>
                              </Collapsible>

                              <Collapsible open={isStraightOpen} onOpenChange={setIsStraightOpen}>
                                <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                  <span>Straight and Center</span>
                                  <ChevronDown className={`h-4 w-4 transition-transform ${isStraightOpen ? 'rotate-180' : ''}`} />
                                </CollapsibleTrigger>
                                <CollapsibleContent className="pl-4 pr-2 pb-2">
                                  <SelectGroup>
                                    <SelectItem value="straight">Straight</SelectItem>
                                      <SelectItem value="mid_on">Mid On</SelectItem>
                                      <SelectItem value="long_on">Long On</SelectItem>
                                      <SelectItem value="long_off">Long Off</SelectItem>
                                  </SelectGroup>
                                </CollapsibleContent>
                              </Collapsible>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                          <span className="font-semibold">Total Score:</span>
                          <span className="text-lg font-bold text-green-600">
                            {calculateScoreComponents().teamRuns} runs
                          </span>
                        </div>
                        <Button 
                          onClick={editingBall ? handleUpdateScore : handleAddScore}
                            className={`w-full ${editingBall ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'}`}
                          disabled={isLoading}
                        >
                          {editingBall ? 'Update Score' : 'Add Score'} ({calculateScoreComponents().teamRuns} runs)
                        </Button>
                        
                        {editingBall && (
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              setEditingBall(null);
                              setSelectedRuns(0);
                              setSelectedBonusBoundary(0);
                              setSelectedExtras(0);
                              setExtraType('');
                              setOverthrows(0);
                              setBallDirection('');
                              setWicketType('');
                              setRunOutPlayerId('');
                            }}
                            className="w-full"
                          >
                            Cancel Edit
                          </Button>
                        )}
                        </div>

                        <div className="flex flex-col gap-2">
                          <Dialog open={showWicketDialog} onOpenChange={setShowWicketDialog}>
                            <DialogTrigger asChild>
                              <Button 
                                variant="destructive" 
                                onClick={() => setShowWicketDialog(true)}
                                disabled={!currentStriker || !currentNonStriker || !currentBowler || isLoading}
                                className="w-full"
                              >
                                Record Wicket
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Record Wicket</DialogTitle>
                              </DialogHeader>
                          <div className="space-y-4 relative">
                            {isLoading && (
                              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-50 rounded-lg">
                                <div className="flex flex-col items-center gap-2">
                                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cricket-green"></div>
                                  <span className="text-sm text-gray-600">Recording wicket...</span>
                                </div>
                              </div>
                            )}
                            <div className="space-y-2">
                              <Label>Wicket Type</Label>
                              <Select value={wicketType} onValueChange={setWicketType} disabled={isLoading}>
                                <SelectTrigger>
                                      <SelectValue placeholder="Select wicket type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="bowled">Bowled</SelectItem>
                                  <SelectItem value="caught">Caught</SelectItem>
                                      <SelectItem value="lbw">LBW</SelectItem>
                                      <SelectItem value="run_out">Run Out</SelectItem>
                                      <SelectItem value="stumped">Stumped</SelectItem>
                                      <SelectItem value="hit_wicket">Hit Wicket</SelectItem>
                                  <SelectItem value="obstructing">Obstructing the Field</SelectItem>
                                    <SelectItem value="timed_out">Timed Out</SelectItem>
                                    <SelectItem value="retired_out">Retired Out</SelectItem>
                                    <SelectItem value="handled_ball">Handled the Ball</SelectItem>
                                    <SelectItem value="hit_ball_twice">Hit the Ball Twice</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                            {wicketType && (
                              <div className="space-y-4">
                                {wicketType === 'run_out' && (
                                  <div className="space-y-4">
                                  <div className="grid grid-cols-4 items-center gap-4">
                                    <label htmlFor="runOutPlayer" className="text-right">
                                      Player Out
                                    </label>
                                    <Select onValueChange={setRunOutPlayerId} disabled={isLoading}>
                                      <SelectTrigger className="col-span-3">
                                        <SelectValue placeholder="Select player out" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {currentStriker && (
                                          <SelectItem value={currentStriker}>
                                            {getCurrentPlayerName(currentStriker)} (Striker)
                                          </SelectItem>
                                        )}
                                        {currentNonStriker && (
                                          <SelectItem value={currentNonStriker}>
                                            {getCurrentPlayerName(currentNonStriker)} (Non-Striker)
                                          </SelectItem>
                                        )}
                                      </SelectContent>
                                    </Select>
                                    </div>
                                    <div className="grid grid-cols-4 items-center gap-4">
                                      <label htmlFor="runOutAssistType" className="text-right">
                                        Assist Type
                                      </label>
                                      <Select onValueChange={setRunOutAssistType} disabled={isLoading}>
                                        <SelectTrigger className="col-span-3">
                                          <SelectValue placeholder="Select assist type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="direct_hit">Direct Hit Run Out</SelectItem>
                                          <SelectItem value="throw_to_keeper">Throw to Keeper Run Out</SelectItem>
                                          <SelectItem value="throw_to_bowler">Throw to Bowler Run Out</SelectItem>
                                          <SelectItem value="relay_throw">Relay Throw Run Out</SelectItem>
                                          <SelectItem value="keeper_flick">Keeper Flick Run Out</SelectItem>
                                          <SelectItem value="bowler_solo">Bowler Solo Run Out</SelectItem>
                                          <SelectItem value="fielder_solo">Fielder Solo Run Out (No Throw)</SelectItem>
                                          <SelectItem value="with_obstruction">Run Out with Obstruction</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {runOutAssistType && (
                                      <div className="space-y-4 pl-4 border-l-2 border-gray-200">
                                        {RUN_OUT_ASSIST_FIELDS[runOutAssistType as keyof typeof RUN_OUT_ASSIST_FIELDS].map((field) => (
                                          <div key={field.id} className="grid grid-cols-4 items-center gap-4">
                                            <label htmlFor={field.id} className="text-right">
                                              {field.label}
                                            </label>
                                            <Select 
                                              value={runOutAssistFields[field.id] || ''} 
                                              onValueChange={(value) => handleRunOutAssistFieldChange(field.id, value)}
                                              disabled={isLoading}
                                            >
                                              <SelectTrigger className="col-span-3">
                                                <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {field.id.includes('keeper') && currentBowler && (
                                                  <SelectItem value={currentBowler}>
                                                    {getCurrentPlayerName(currentBowler)} (Keeper)
                                                  </SelectItem>
                                                )}
                                                {field.id.includes('bowler') && currentBowler && (
                                                  <SelectItem value={currentBowler}>
                                                    {getCurrentPlayerName(currentBowler)} (Bowler)
                                                  </SelectItem>
                                                )}

                                              </SelectContent>
                                            </Select>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}



                                <div className="space-y-4">
                                  <div className="flex items-center justify-between">
                                    <Label>Review Used</Label>
                                    <div className="flex items-center space-x-2">
                                      <Button
                                        variant={reviewUsed ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setReviewUsed(true)}
                                        disabled={isLoading}
                                      >
                                        Yes
                                      </Button>
                                      <Button
                                        variant={!reviewUsed ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setReviewUsed(false)}
                                        disabled={isLoading}
                                      >
                                        No
                                      </Button>
                                    </div>
                                  </div>

                                  {reviewUsed && (
                                    <div className="space-y-2">
                                      <Label>Review Result</Label>
                                      <Select 
                                        value={reviewResult} 
                                        onValueChange={(value: 'umpires_call' | 'out' | 'not_out' | 'overturned' | 'retained' | 'lost') => setReviewResult(value)} 
                                        disabled={isLoading}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select review result" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="umpires_call">Umpire's Call</SelectItem>
                                          <SelectItem value="out">Out</SelectItem>
                                          <SelectItem value="not_out">Not Out</SelectItem>
                                          <SelectItem value="overturned">Overturned</SelectItem>
                                          <SelectItem value="retained">Retained</SelectItem>
                                          <SelectItem value="lost">Lost</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}
                                </div>

                                <div className="text-sm text-gray-500">
                                  {currentBowler && (
                                    <div>Current Bowler: {getCurrentPlayerName(currentBowler)}</div>
                                  )}

              </div>

                                <Button onClick={handleWicket} disabled={isLoading} className="w-full">
                                  {isLoading ? 'Recording Wicket...' : 'Confirm Wicket'}
                                </Button>
                              </div>
                            )}
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Button 
                            className="bg-cricket-accent text-white hover:bg-cricket-accent/90"
                            onClick={() => setShowInningsChangeDialog(true)}
                            disabled={isLoading}
                          >
                            End This Inning
                          </Button>
                        </div>
                      </div>

                      {/* Right side - Advanced input fields (only shown in Advanced Mode) */}
                      {isAdvancedMode && (
                        <div className="space-y-6">
                          <div>
                            <Label>Shot Type</Label>
                            <Select value={shotType} onValueChange={setShotType} disabled={isLoading}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select shot type" />
                              </SelectTrigger>
                              <SelectContent>
                                <Collapsible open={isOffsideOpen} onOpenChange={setIsOffsideOpen}>
                                  <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                    <span>Front Foot</span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isOffsideOpen ? 'rotate-180' : ''}`} />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="pl-4 pr-2 pb-2">
                                    <SelectGroup>
                                      {SHOT_TYPES.FRONT_FOOT.map((type) => (
                                        <SelectItem key={type} value={type}>
                                          {SHOT_TYPE_DISPLAY[type]}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </CollapsibleContent>
                                </Collapsible>
                                
                                <Collapsible open={isLegsideOpen} onOpenChange={setIsLegsideOpen}>
                                  <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                    <span>Back Foot</span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isLegsideOpen ? 'rotate-180' : ''}`} />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="pl-4 pr-2 pb-2">
                                    <SelectGroup>
                                      {SHOT_TYPES.BACK_FOOT.map((type) => (
                                        <SelectItem key={type} value={type}>
                                          {SHOT_TYPE_DISPLAY[type]}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </CollapsibleContent>
                                </Collapsible>

                                <Collapsible open={isStraightOpen} onOpenChange={setIsStraightOpen}>
                                  <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                    <span>Lofted</span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isStraightOpen ? 'rotate-180' : ''}`} />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="pl-4 pr-2 pb-2">
                                    <SelectGroup>
                                      {SHOT_TYPES.LOFTED.map((type) => (
                                        <SelectItem key={type} value={type}>
                                          {SHOT_TYPE_DISPLAY[type]}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </CollapsibleContent>
                                </Collapsible>

                                <Collapsible open={isMiscOpen} onOpenChange={setIsMiscOpen}>
                                  <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                    <span>Others</span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isMiscOpen ? 'rotate-180' : ''}`} />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="pl-4 pr-2 pb-2">
                                    <SelectGroup>
                                      {SHOT_TYPES.OTHERS.map((type) => (
                                        <SelectItem key={type} value={type}>
                                          {SHOT_TYPE_DISPLAY[type]}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </CollapsibleContent>
                                </Collapsible>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label>Ball Length and Variation</Label>
                            <Select value={ballLength} onValueChange={setBallLength} disabled={isLoading}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select ball length" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>By Length</SelectLabel>
                                  {BALL_LENGTHS.map((length) => (
                                    <SelectItem key={length} value={length}>
                                      {BALL_LENGTH_DISPLAY[length]}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                                  </div>

                                <div>
                            <Label>Ball Type (Variation or Spin)</Label>
                            <Select value={ballType} onValueChange={setBallType} disabled={isLoading}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select ball type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>Seamers</SelectLabel>
                                  {BALL_TYPES_SEAMERS.map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {BALL_TYPE_DISPLAY[type]}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                                <SelectGroup>
                                  <SelectLabel>Spinners</SelectLabel>
                                  {BALL_TYPES_SPINNERS.map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {BALL_TYPE_DISPLAY[type]}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                                </div>

                                <div>
                            <Label>Fielding Position</Label>
                            <Select value={fieldingPosition} onValueChange={setFieldingPosition} disabled={isLoading}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select fielding position" />
                              </SelectTrigger>
                              <SelectContent>
                                <Collapsible open={isOffsideOpen} onOpenChange={setIsOffsideOpen}>
                                  <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                    <span>Off Side</span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isOffsideOpen ? 'rotate-180' : ''}`} />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="pl-4 pr-2 pb-2">
                                    <SelectGroup>
                                      {FIELDING_POSITIONS_OFF_SIDE.map((position) => (
                                        <SelectItem key={position} value={position}>
                                          {FIELDING_POSITION_DISPLAY[position]}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </CollapsibleContent>
                                </Collapsible>

                                <Collapsible open={isLegsideOpen} onOpenChange={setIsLegsideOpen}>
                                  <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                    <span>Leg Side</span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isLegsideOpen ? 'rotate-180' : ''}`} />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="pl-4 pr-2 pb-2">
                                    <SelectGroup>
                                      {FIELDING_POSITIONS_LEG_SIDE.map((position) => (
                                        <SelectItem key={position} value={position}>
                                          {FIELDING_POSITION_DISPLAY[position]}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </CollapsibleContent>
                                </Collapsible>

                                <Collapsible open={isStraightOpen} onOpenChange={setIsStraightOpen}>
                                  <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold hover:bg-gray-100 rounded-md">
                                    <span>Special or Rare Positions</span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isStraightOpen ? 'rotate-180' : ''}`} />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="pl-4 pr-2 pb-2">
                                    <SelectGroup>
                                      {FIELDING_POSITIONS_SPECIAL.map((position) => (
                                        <SelectItem key={position} value={position}>
                                          {FIELDING_POSITION_DISPLAY[position]}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </CollapsibleContent>
                                </Collapsible>
                              </SelectContent>
                            </Select>
                                  </div>

                          {fieldingPosition === 'others' && (
                              <div>
                              <Label>Custom Fielding Position</Label>
                                <Input
                              value={customFieldingPosition}
                              onChange={(e) => setCustomFieldingPosition(e.target.value)}
                              placeholder="Enter custom fielding position"
                              disabled={isLoading}
                              />
                              </div>
                          )}
                                      </div>
                  )}
                </div>
              </CardContent>
                    {isLoading && (
                      <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-50 rounded-lg">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-cricket-green"></div>
                      </div>
                    )}
                </Card>
              </div>
          )}
        </div>
        </TabsContent>
        <TabsContent value="batting-stats">
          <Card>
            <CardHeader>
              <CardTitle>Batting</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batsman</TableHead>
                    <TableHead className="text-right">Runs</TableHead>
                    <TableHead className="text-right">Balls</TableHead>
                    <TableHead className="text-right">4s</TableHead>
                    <TableHead className="text-right">6s</TableHead>
                    <TableHead className="text-right">SR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batsmanStats.map(stats => {
                    const player = availableBatsmen.find(p => p.id === stats.playerId);
                    if (!player) return null;
                    return (
                      <TableRow key={player.id}>
                        <TableCell>
                          <div className="flex items-center">
                            <span className="font-medium">{player.name}</span>
                            {player.isCaptain && <span className="ml-1 text-xs text-gray-500">(C)</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{stats.runs}</TableCell>
                        <TableCell className="text-right">{stats.balls}</TableCell>
                        <TableCell className="text-right">{stats.fours}</TableCell>
                        <TableCell className="text-right">{stats.sixes}</TableCell>
                        <TableCell className="text-right">
                          {(stats.balls > 0 ? (stats.runs / stats.balls) * 100 : 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="bowling-stats">
          <Card>
            <CardHeader>
              <CardTitle>Bowling</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bowler</TableHead>
                    <TableHead className="text-right">Overs</TableHead>
                    <TableHead className="text-right">Maidens</TableHead>
                    <TableHead className="text-right">Runs</TableHead>
                    <TableHead className="text-right">Wickets</TableHead>
                    <TableHead className="text-right">Economy</TableHead>
                    <TableHead className="text-right">Dots</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bowlerStats.map(stats => {
                    const bowler = availableBowlers.find(p => p.id === stats.playerId);
                    return (
                      <TableRow key={stats.playerId}>
                        <TableCell>{bowler ? bowler.name : stats.playerId}</TableCell>
                        <TableCell className="text-right">{stats.overs}</TableCell>
                        <TableCell className="text-right">{stats.maidens}</TableCell>
                        <TableCell className="text-right">{stats.runsGiven}</TableCell>
                        <TableCell className="text-right">{stats.wickets}</TableCell>
                        <TableCell className="text-right">{stats.economyRate.toFixed(1)}</TableCell>
                        <TableCell className="text-right">{stats.dots}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="score-history">
          <ScoreHistoryManager
            scoreHistory={scoreHistory}
            teams={teams}
            match={match}
            onEditEntry={handleEditEntry}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            editingEntry={editingEntry}
            editingEntryId={editingEntryId}
            onDeleteEntry={handleDeleteSingleEntry}
            isLoading={isLoading}
            onBallClick={handleBallClick}
            onUndoToIndex={confirmUndo}
            onRestoreEntry={handleRestoreEntry}
            players={[...(team1?.players || []), ...(team2?.players || [])]}
          />
        </TabsContent>
        <TabsContent value="match-info">
          <div className="p-4 space-y-6">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Match Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2">
                  <div className="text-sm text-gray-500">• Match Type</div>
                  <div className="font-medium text-right">{capitalizeFirst(match.matchType)} ({match.totalInnings || 2} Innings)</div>
                  <div className="text-sm text-gray-500">• Total Overs</div>
                  <div className="font-medium text-right">{match.totalOvers}</div>

                  <div className="text-sm text-gray-500">• City</div>
                  <div className="font-medium text-right">{match.city}</div>
                  <div className="text-sm text-gray-500">• Balls per Over</div>
                  <div className="font-medium text-right">{match.ballsPerOver}</div>

                  <div className="text-sm text-gray-500">• Date</div>
                  <div className="font-medium text-right">
                    {match.matchDate ? format(new Date(match.matchDate), 'PPPP') : 'N/A'}
                  </div>
                  <div className="text-sm text-gray-500">• Wickets per Innings</div>
                  <div className="font-medium text-right">{match.wicketsPerInnings || match.totalWickets || 10}</div>

                  <div className="text-sm text-gray-500">• Status</div>
                  <div className="font-medium flex items-center gap-2 justify-end">
                    {match.status === 'live' ? (
                      <Badge variant="secondary" className="bg-green-500 text-white">Live</Badge>
                    ) : match.status === 'completed' ? (
                      <Badge variant="secondary" className="bg-blue-500 text-white">Completed</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-gray-500 text-white">Upcoming</Badge>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">• Current Innings</div>
                  <div className="font-medium text-right">
                    {getOrdinal(match.currentInnings)} Innings - {match.currentBattingTeam === team1?.id ? team1?.name : team2?.name}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[team1, team2].filter(Boolean).map(team => (
                <Card key={team.id} className="shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {team.name}
                      {team.color && (
                        <div 
                          className="w-4 h-4 rounded-md border-2 border-gray-300 dark:border-gray-600"
                          style={{ backgroundColor: team.color }}
                        />
                      )}
                    </CardTitle>
                    <p className="text-sm text-gray-500">{team.shortCode}</p>
                  </CardHeader>
                  <CardContent>
                    <h3 className="text-md font-semibold mb-2">Players</h3>
                    <div className="space-y-1">
                      {team.players?.slice().sort((a, b) => {
                        const pa = getPlayerSortPriority(a, team, match);
                        const pb = getPlayerSortPriority(b, team, match);
                        return pa - pb;
                      }).map((player, index) => {
                        const isCaptain = player.id === (team.id === match.team1Id ? match.team1CaptainId : match.team2CaptainId);
                        const isViceCaptain = player.id === (team.id === match.team1Id ? match.team1ViceCaptainId : match.team2ViceCaptainId);
                        const isWicketKeeper = player.role === 'wicket-keeper';
                        const isCurrentlyBatting = player.id === liveRoles.striker?.id || player.id === liveRoles.nonStriker?.id;
                        return (
                          <div 
                            key={player.id} 
                            className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50"
                            style={{
                              backgroundColor: team.color 
                                ? (index % 2 === 0 ? `${team.color}20` : `${team.color}10`) 
                                : 'transparent',
                              transition: 'background-color 0.2s ease-in-out',
                            }}
                          >
                            <span className="font-medium flex items-center gap-1">
                              {player.name}
                              {isCaptain && <span className="ml-1 px-2 py-0.5 rounded text-xs bg-yellow-400 text-black">C</span>}
                              {isViceCaptain && <span className="ml-1 px-2 py-0.5 rounded text-xs bg-blue-400 text-white">VC</span>}
                              {isWicketKeeper && <span title="Wicketkeeper" className="ml-1 text-xs">†</span>}
                              {isCurrentlyBatting && <span title="Currently Batting" className="ml-1 text-xs">*</span>}
                            </span>
                            <Badge variant="outline">{player.role}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {/* Umpire Details Section */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Umpires</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-semibold">On-field Umpire 1:</span>
                    <span className="ml-2">{getUmpireName('On-field 1')}</span>
                  </div>
                  <div>
                    <span className="font-semibold">On-field Umpire 2:</span>
                    <span className="ml-2">{getUmpireName('On-field 2')}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Third Umpire:</span>
                    <span className="ml-2">{getUmpireName('Third')}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Fourth Umpire:</span>
                    <span className="ml-2">{getUmpireName('Fourth')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={showBowlerModal} onOpenChange={setShowBowlerModal}>
        <DialogContent>
          <DialogTitle>Select Next Bowler</DialogTitle>
          <Select value={nextBowler || ''} onValueChange={setNextBowler}>
            <SelectTrigger className="w-full">Select Bowler</SelectTrigger>
            <SelectContent>
              {availableBowlers.map(bowler => (
                <SelectItem key={bowler.id} value={bowler.id}>
                  {bowler.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => {
            if (nextBowler) {
              setCurrentBowler(nextBowler);
              setShowBowlerModal(false);
            }
          }} disabled={!nextBowler}>
            Confirm
          </Button>
        </DialogContent>
      </Dialog>

      {/* Match Completion Confirmation Dialog */}
      <MatchCompletionDialog
        trigger={<div style={{ display: 'none' }} />} // Hidden trigger, controlled by state
        open={showMatchCompletionDialog}
        onOpenChange={setShowMatchCompletionDialog}
        onConfirm={handleMatchCompletionConfirmed}
        onCancel={() => {
          setShowMatchCompletionDialog(false);
          setPendingMatchCompletionReason('');
        }}
        matchName={`${team1?.name} vs ${team2?.name}`}
        reason={pendingMatchCompletionReason}
      />
    </div>
  );
};
